import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Users, FileText, Calendar, MessageSquare, TrendingUp, Plus, Pencil, Trash2, Link as LinkIcon, Sparkles, Wand2, Rocket, Target, CheckCircle2, AlertCircle, Database, AlertTriangle, RefreshCw, Check, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import ImageInput from "@/components/ImageInput";

const EMPTY_DEAL = {
  brand: "", model: "", trim: "", year: 2025,
  image_url: "", msrp: 50000, monthly_payment: 599, due_at_signing: 3995,
  lease_term_months: 36, annual_mileage: 10000, expiration_date: "2026-03-31",
  region: "Nationwide", dealer_source: "555 Auto Network", incentives: "",
  vehicle_type: "sedan", is_luxury: false, is_ev: false, featured: false,
  status: "active", savings: 4000,
};

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [launch, setLaunch] = useState(null);
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [deals, setDeals] = useState([]);
  const [dealForm, setDealForm] = useState(EMPTY_DEAL);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const reload = async () => {
    const [s, l, b, d, lr] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/leads"),
      api.get("/admin/bookings"),
      api.get("/admin/lease-deals"),
      api.get("/admin/launch-readiness").catch(() => ({ data: null })),
    ]);
    setStats(s.data); setLeads(l.data); setBookings(b.data); setDeals(d.data); setLaunch(lr.data);
  };

  useEffect(() => { reload().catch(() => {}); }, []);

  const openCreate = () => { setDealForm(EMPTY_DEAL); setEditing(null); setDialogOpen(true); };
  const openEdit = (d) => { setDealForm(d); setEditing(d.id); setDialogOpen(true); };

  const saveDeal = async () => {
    const body = { ...dealForm, year: +dealForm.year, msrp: +dealForm.msrp, monthly_payment: +dealForm.monthly_payment, due_at_signing: +dealForm.due_at_signing, lease_term_months: +dealForm.lease_term_months, annual_mileage: +dealForm.annual_mileage, savings: +dealForm.savings };
    try {
      if (editing) await api.put(`/admin/lease-deals/${editing}`, body);
      else await api.post("/admin/lease-deals", body);
      toast.success(editing ? "Deal updated" : "Deal created");
      setDialogOpen(false);
      reload();
    } catch (e) { toast.error("Save failed"); }
  };

  const deleteDeal = async (id) => {
    if (!confirm("Delete this deal?")) return;
    await api.delete(`/admin/lease-deals/${id}`);
    toast.success("Deleted");
    reload();
  };

  return (
    <div className="max-w-[1500px] mx-auto px-5 lg:px-10 py-12" data-testid="admin-page">
      <p className="label-mono mb-3">Admin Control Room</p>
      <h1 className="font-display text-4xl md:text-5xl font-light tracking-tighter mb-6">Pipeline overview.</h1>
      <VerifiedToggle />

      {/* KPIs */}
      {stats && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          <Kpi icon={Users} label="Total Users" value={stats.users_total} testId="kpi-users" />
          <Kpi icon={TrendingUp} label="Active Deals" value={stats.deals_total} testId="kpi-deals" />
          <Kpi icon={FileText} label="Leads" value={stats.leads_total} testId="kpi-leads" />
          <Kpi icon={Calendar} label="Bookings" value={stats.bookings_total} testId="kpi-bookings" />
          <Kpi icon={MessageSquare} label="AI Chats" value={stats.chats_total} testId="kpi-chats" />
        </div>
      )}

      <Tabs defaultValue="launch" className="w-full">
        <TabsList className="bg-[#0A0A0E] border border-[#1A1A24] mb-6 flex-wrap h-auto">
          <TabsTrigger value="launch" data-testid="admin-tab-launch"><Rocket className="w-3 h-3 mr-1" />Launch</TabsTrigger>
          <TabsTrigger value="leads" data-testid="admin-tab-leads">Leads</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="admin-tab-bookings">Bookings</TabsTrigger>
          <TabsTrigger value="deals" data-testid="admin-tab-deals">Lease Deals</TabsTrigger>
          <TabsTrigger value="agreements" data-testid="admin-tab-agreements">Agreements</TabsTrigger>
          <TabsTrigger value="dealers" data-testid="admin-tab-dealers">Dealers</TabsTrigger>
          <TabsTrigger value="library" data-testid="admin-tab-library"><LinkIcon className="w-3 h-3 mr-1" />Source Library</TabsTrigger>
          <TabsTrigger value="quick" data-testid="admin-tab-quick"><Plus className="w-3 h-3 mr-1" />Quick Entry</TabsTrigger>
          <TabsTrigger value="authorized" data-testid="admin-tab-authorized"><Database className="w-3 h-3 mr-1" />Authorized Feed</TabsTrigger>
          <TabsTrigger value="exceptions" data-testid="admin-tab-exceptions"><AlertTriangle className="w-3 h-3 mr-1" />Exceptions</TabsTrigger>
          <TabsTrigger value="sources" data-testid="admin-tab-sources">Data Sources</TabsTrigger>
          <TabsTrigger value="emails" data-testid="admin-tab-emails">Email Queue</TabsTrigger>
          <TabsTrigger value="payments" data-testid="admin-tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="admin-tab-analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="launch">
          <LaunchDashboard launch={launch} deals={deals} onReload={reload} />
        </TabsContent>

        <TabsContent value="leads">
          <LeadsTab leads={leads} onReload={reload} />
        </TabsContent>

        <TabsContent value="bookings">
          <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-gray-400">
                <tr><th className="text-left p-4">When</th><th className="text-left p-4">Type</th><th className="text-left p-4">Name</th><th className="text-left p-4">Email</th><th className="text-left p-4">Phone</th></tr>
              </thead>
              <tbody>
                {bookings.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-gray-500">No bookings yet</td></tr>}
                {bookings.map((b) => (
                  <tr key={b.id} className="border-t border-white/5">
                    <td className="p-4 text-gray-400 text-xs">{new Date(b.booking_datetime).toLocaleString()}</td>
                    <td className="p-4 label-mono">{b.consult_type}</td>
                    <td className="p-4">{b.name}</td>
                    <td className="p-4 text-gray-400">{b.email}</td>
                    <td className="p-4 text-gray-400">{b.phone}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="deals">
          <div className="flex justify-end mb-4 gap-2">
            <OemExtractorDialog onSaved={reload} />
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <button onClick={openCreate} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full silver-gradient text-[#050507] font-bold text-sm" data-testid="admin-new-deal">
                  <Plus className="w-4 h-4" /> New Deal
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[#0A0A0E] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Deal" : "New Deal"}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  {[
                    ["brand", "Brand"], ["model", "Model"], ["trim", "Trim"], ["year", "Year", "number"],
                    ["msrp", "MSRP", "number"], ["monthly_payment", "Monthly $", "number"], ["due_at_signing", "Due Signing $", "number"],
                    ["lease_term_months", "Term (mo)", "number"], ["annual_mileage", "Annual Miles", "number"], ["savings", "Savings $", "number"],
                    ["expiration_date", "Expires (YYYY-MM-DD)"], ["region", "Region"], ["dealer_source", "Dealer"], ["incentives", "Incentives"],
                  ].map(([k, l, type]) => (
                    <div key={k}>
                      <label className="label-mono mb-1 block">{l}</label>
                      <Input type={type || "text"} value={dealForm[k] ?? ""} onChange={(e) => setDealForm({ ...dealForm, [k]: e.target.value })} className="bg-[#050507] border-[#1A1A24] h-10 text-white" data-testid={`deal-form-${k}`} />
                    </div>
                  ))}
                  <div className="col-span-2">
                    <label className="label-mono mb-1 block">Vehicle Image</label>
                    <ImageInput
                      value={dealForm.image_url}
                      onChange={(v) => setDealForm({ ...dealForm, image_url: v })}
                      testId="deal-form-image"
                      placeholder="Paste an OEM/dealer image URL, or upload a photo"
                    />
                  </div>
                  <div>
                    <label className="label-mono mb-1 block">Vehicle Type</label>
                    <select value={dealForm.vehicle_type} onChange={(e) => setDealForm({ ...dealForm, vehicle_type: e.target.value })} className="w-full bg-[#050507] border border-[#1A1A24] h-10 rounded-md text-white px-3 text-sm">
                      <option value="sedan">Sedan</option><option value="suv">SUV</option><option value="truck">Truck</option><option value="coupe">Coupe</option><option value="ev">EV</option>
                    </select>
                  </div>
                  <div>
                    <label className="label-mono mb-1 block">Status</label>
                    <select value={dealForm.status} onChange={(e) => setDealForm({ ...dealForm, status: e.target.value })} className="w-full bg-[#050507] border border-[#1A1A24] h-10 rounded-md text-white px-3 text-sm">
                      <option value="active">Active</option><option value="expired">Expired</option><option value="pending">Pending</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={dealForm.is_luxury} onChange={(e) => setDealForm({ ...dealForm, is_luxury: e.target.checked })} /> Luxury</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={dealForm.is_ev} onChange={(e) => setDealForm({ ...dealForm, is_ev: e.target.checked })} /> EV</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={dealForm.featured} onChange={(e) => setDealForm({ ...dealForm, featured: e.target.checked })} /> Featured</label>
                </div>
                <button onClick={saveDeal} className="mt-5 w-full silver-gradient text-[#050507] font-bold py-3 rounded-full" data-testid="deal-form-save">
                  {editing ? "Save Changes" : "Create Deal"}
                </button>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-gray-400">
                <tr><th className="text-left p-3">Vehicle</th><th className="text-left p-3">Monthly</th><th className="text-left p-3">Status</th><th className="text-left p-3">Intel</th><th className="text-left p-3">Source</th><th className="text-left p-3">Flags</th><th className="text-left p-3"></th></tr>
              </thead>
              <tbody>
                {deals.map((d) => (
                  <tr key={d.id} className="border-t border-white/5" data-testid={`admin-deal-row-${d.id}`}>
                    <td className="p-3"><div className="font-display">{d.brand} {d.model}</div><div className="text-xs text-gray-500">{d.trim} · {d.year}</div></td>
                    <td className="p-3">${d.monthly_payment} · DAS ${d.due_at_signing}</td>
                    <td className="p-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                        d.verification_status?.includes("verified") ? "bg-emerald-500/20 text-emerald-300"
                        : d.verification_status === "expired" ? "bg-gray-500/20 text-gray-300"
                        : d.verification_status === "rejected" ? "bg-red-500/20 text-red-300"
                        : "bg-yellow-500/20 text-yellow-300"
                      }`}>{(d.verification_status || "pending").replace(/_/g, " ")}</span>
                    </td>
                    <td className="p-3">
                      {d.intelligence_score ? (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                          d.intelligence_score === "great" ? "bg-emerald-500/25 text-emerald-200"
                          : d.intelligence_score === "good" ? "bg-blue-500/25 text-blue-200"
                          : "bg-gray-500/25 text-gray-200"
                        }`} title={d.intelligence_reason || ""}>{d.intelligence_score.toUpperCase()}</span>
                      ) : <span className="text-[10px] text-gray-500">—</span>}
                    </td>
                    <td className="p-3 text-xs text-gray-400 max-w-[180px] truncate" title={d.source_url}>{d.source_name || "—"}</td>
                    <td className="p-3 text-xs">
                      {d.featured && <span className="mr-1 px-1.5 py-0.5 rounded silver-gradient text-[#050507]">FT</span>}
                      {d.is_exotic && <span className="mr-1 px-1.5 py-0.5 rounded bg-yellow-500/30 text-yellow-200">EXO</span>}
                      {d.is_ev && <span className="mr-1 px-1.5 py-0.5 rounded bg-emerald-500/30 text-emerald-300">EV</span>}
                      {d.is_luxury && <span className="px-1.5 py-0.5 rounded bg-white/10">LUX</span>}
                    </td>
                    <td className="p-3 flex flex-wrap gap-1">
                      <button onClick={async () => { const u = prompt("Source URL (e.g. https://bmwusa.com/offers):", d.source_url || ""); const n = prompt("Source name:", d.source_name || ""); await api.post(`/admin/lease-deals/${d.id}/approve`, { verification_status: "oem_verified", source_url: u || "", source_name: n || "Manual" }); toast.success("Approved as OEM"); reload(); }} className="px-2 py-1 rounded bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-[10px]" data-testid={`admin-approve-${d.id}`}>Approve</button>
                      <button onClick={async () => { await api.post(`/admin/lease-deals/${d.id}/approve`, { verification_status: "rejected" }); toast.success("Rejected"); reload(); }} className="px-2 py-1 rounded bg-red-500/20 hover:bg-red-500/30 text-red-300 text-[10px]" data-testid={`admin-reject-${d.id}`}>Reject</button>
                      <button onClick={async () => { await api.post(`/admin/lease-deals/${d.id}/approve`, { verification_status: "expired" }); toast.success("Marked expired"); reload(); }} className="px-2 py-1 rounded bg-gray-500/20 hover:bg-gray-500/30 text-gray-300 text-[10px]" data-testid={`admin-expire-${d.id}`}>Expire</button>
                      <button onClick={async () => { try { const { data } = await api.post(`/admin/lease-deals/${d.id}/score-intelligence`); toast.success(`Scored ${data.rating?.toUpperCase()}`); reload(); } catch { toast.error("Score failed"); } }} className="px-2 py-1 rounded bg-violet-500/20 hover:bg-violet-500/30 text-violet-300 text-[10px] inline-flex items-center gap-1" data-testid={`admin-score-${d.id}`}><Sparkles className="w-3 h-3" />Score</button>
                      <button onClick={() => openEdit(d)} className="p-1.5 rounded-full bg-white/5 hover:bg-white/10" data-testid={`admin-edit-${d.id}`}><Pencil className="w-3 h-3" /></button>
                      <button onClick={() => deleteDeal(d.id)} className="p-1.5 rounded-full bg-white/5 hover:bg-red-500/20" data-testid={`admin-delete-${d.id}`}><Trash2 className="w-3 h-3" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="sources">
          <DataSourcesTab />
        </TabsContent>

        <TabsContent value="library">
          <SourceLibraryTab />
        </TabsContent>

        <TabsContent value="quick">
          <QuickEntryTab />
        </TabsContent>

        <TabsContent value="authorized">
          <AuthorizedFeedTab />
        </TabsContent>

        <TabsContent value="exceptions">
          <ExceptionQueueTab />
        </TabsContent>

        <TabsContent value="agreements">
          <AgreementsTab />
        </TabsContent>

        <TabsContent value="dealers">
          <DealersTab />
        </TabsContent>

        <TabsContent value="emails">
          <EmailQueueTab />
        </TabsContent>

        <TabsContent value="payments">
          <PaymentsTab />
        </TabsContent>

        <TabsContent value="analytics">
          {stats && (
            <div className="grid md:grid-cols-2 gap-5">
              <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-7">
                <div className="font-display text-xl mb-5">Leads by Type</div>
                <div className="space-y-3">
                  {Object.entries(stats.leads_by_type).map(([k, v]) => (
                    <div key={k}>
                      <div className="flex justify-between text-sm mb-1"><span className="capitalize">{k.replace(/_/g, " ")}</span><span>{v}</span></div>
                      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full silver-gradient" style={{ width: `${Math.min(100, (v / Math.max(1, stats.leads_total)) * 100)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-7">
                <div className="font-display text-xl mb-5">Top Deals by Clicks</div>
                <ul className="space-y-3">
                  {stats.top_deals.map((d) => (
                    <li key={d.id} className="flex justify-between text-sm">
                      <span>{d.brand} {d.model}</span>
                      <span className="text-gray-400">{d.clicks || 0} clicks</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-5" data-testid={testId}>
      <Icon className="w-4 h-4 text-silver mb-3" />
      <div className="label-mono mb-1">{label}</div>
      <div className="font-display text-3xl">{value}</div>
    </div>
  );
}

function VerifiedToggle() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/platform-settings").then((r) => setSettings(r.data)); }, []);
  if (!settings) return null;
  const flip = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/platform-settings", { verified_deals_only: !settings.verified_deals_only });
      setSettings(data);
      toast.success(`Verified Deals Only: ${data.verified_deals_only ? "ON" : "OFF"}`);
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mb-8 glass rounded-2xl p-5 flex items-center gap-4" data-testid="verified-only-toggle">
      <div className={`w-3 h-3 rounded-full ${settings.verified_deals_only ? "bg-emerald-400 animate-pulse" : "bg-gray-500"}`} />
      <div className="flex-1">
        <div className="font-display text-base">Verified Deals Only: <span className={settings.verified_deals_only ? "text-emerald-400" : "text-yellow-400"}>{settings.verified_deals_only ? "ON" : "OFF"}</span></div>
        <p className="text-xs text-gray-400 mt-0.5">{settings.verified_deals_only ? "Public marketplace shows only OEM/Dealer/Admin-verified deals." : "All deals (including pending) are publicly visible. Turn ON before launch."}</p>
      </div>
      <button onClick={flip} disabled={busy} className="px-5 py-2.5 rounded-full silver-gradient text-[#050507] text-xs font-bold disabled:opacity-50" data-testid="verified-only-flip">
        {busy ? "…" : `Turn ${settings.verified_deals_only ? "OFF" : "ON"}`}
      </button>
    </div>
  );
}

const EMPTY_SOURCE = { name: "", type: "oem_feed", endpoint: "", brand_scope: "", notes: "", enabled: true };

function DataSourcesTab() {
  const [sources, setSources] = useState([]);
  const [adapters, setAdapters] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_SOURCE);
  const [editingId, setEditingId] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const csvRef = useRef(null);

  const load = () => Promise.all([
    api.get("/admin/data-sources").then((r) => setSources(r.data)),
    api.get("/admin/feed-adapters").then((r) => setAdapters(r.data)).catch(() => setAdapters([])),
  ]);
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editingId) await api.put(`/admin/data-sources/${editingId}`, form);
      else await api.post("/admin/data-sources", form);
      toast.success(editingId ? "Source updated" : "Source added");
      setOpen(false);
      load();
    } catch { toast.error("Save failed"); }
  };

  const refresh = async (id) => {
    try {
      const { data } = await api.post(`/admin/data-sources/${id}/refresh`);
      toast.success(data.message || "Refreshed");
      load();
    } catch { toast.error("Refresh failed"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this data source?")) return;
    await api.delete(`/admin/data-sources/${id}`);
    toast.success("Deleted");
    load();
  };

  const importCsv = async (file) => {
    if (!file) return;
    setImportBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/lease-deals/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported} deals${data.errors.length ? ` (${data.errors.length} errors)` : ""}`);
    } catch { toast.error("Import failed"); }
    finally { setImportBusy(false); if (csvRef.current) csvRef.current.value = ""; }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-end gap-2">
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/csv-template`}
          target="_blank"
          rel="noreferrer"
          className="px-4 py-2 rounded-full border border-white/15 text-xs hover:bg-white/5"
          data-testid="admin-csv-template-download"
        >
          ↓ CSV Template
        </a>
        <input ref={csvRef} type="file" accept=".csv" hidden onChange={(e) => importCsv(e.target.files?.[0])} data-testid="admin-csv-input" />
        <button onClick={() => csvRef.current?.click()} disabled={importBusy} className="px-4 py-2 rounded-full border border-white/15 text-xs hover:bg-white/5 disabled:opacity-50" data-testid="admin-csv-import">
          {importBusy ? "Importing…" : "Bulk CSV Import"}
        </button>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm(EMPTY_SOURCE); } }}>
          <DialogTrigger asChild>
            <button onClick={() => { setEditingId(null); setForm(EMPTY_SOURCE); setOpen(true); }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full silver-gradient text-[#050507] text-xs font-bold" data-testid="admin-add-source">
              <Plus className="w-3.5 h-3.5" /> Add Source
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0A0E] border-white/10 text-white">
            <DialogHeader><DialogTitle className="font-display">{editingId ? "Edit" : "Add"} Data Source</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-3">
              <Input placeholder="Name (e.g. BMW USA Offers)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#050507] border-[#1A1A24] h-10 text-white" data-testid="source-form-name" />
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full bg-[#050507] border border-[#1A1A24] h-10 rounded-md text-white px-3 text-sm" data-testid="source-form-type">
                <option value="oem_feed">OEM Feed</option>
                <option value="dealer_api">Dealer API</option>
                <option value="affiliate">Affiliate</option>
                <option value="partner_feed">Partner Feed</option>
                <option value="manual_csv">Manual CSV</option>
              </select>
              <Input placeholder="Endpoint URL (optional)" value={form.endpoint} onChange={(e) => setForm({ ...form, endpoint: e.target.value })} className="bg-[#050507] border-[#1A1A24] h-10 text-white" data-testid="source-form-endpoint" />
              <Input placeholder="Brand scope (e.g. BMW, Mini)" value={form.brand_scope} onChange={(e) => setForm({ ...form, brand_scope: e.target.value })} className="bg-[#050507] border-[#1A1A24] h-10 text-white" />
              <textarea rows={2} placeholder="Notes (auth method, refresh schedule…)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-[#050507] border border-[#1A1A24] rounded-md text-white p-3 text-sm" />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} /> Enabled</label>
              <button onClick={save} className="w-full silver-gradient text-[#050507] py-2.5 rounded-full font-bold" data-testid="source-form-save">{editingId ? "Save" : "Create"}</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-gray-400">
            <tr>
              <th className="text-left p-3">Source</th>
              <th className="text-left p-3">Type</th>
              <th className="text-left p-3">Scope</th>
              <th className="text-left p-3">Last Refresh</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3"></th>
            </tr>
          </thead>
          <tbody>
            {sources.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-gray-500">No data sources yet. Add one above — or use bulk CSV import for one-off uploads.</td></tr>}
            {sources.map((s) => (
              <tr key={s.id} className="border-t border-white/5" data-testid={`source-row-${s.id}`}>
                <td className="p-3"><div className="font-display">{s.name}</div><div className="text-xs text-gray-500 truncate max-w-xs">{s.endpoint || s.notes}</div></td>
                <td className="p-3 label-mono">{s.type.replace(/_/g, " ")}</td>
                <td className="p-3 text-gray-400 text-xs">{s.brand_scope || "—"}</td>
                <td className="p-3 text-gray-400 text-xs">{s.last_refresh ? new Date(s.last_refresh).toLocaleString() : "never"}</td>
                <td className="p-3">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.enabled ? "bg-emerald-500/20 text-emerald-400" : "bg-white/10 text-gray-400"}`}>
                    {s.enabled ? s.status || "ready" : "disabled"}
                  </span>
                </td>
                <td className="p-3 flex gap-2">
                  <button onClick={() => refresh(s.id)} className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-xs" data-testid={`source-refresh-${s.id}`}>Refresh</button>
                  <button onClick={() => { setForm(s); setEditingId(s.id); setOpen(true); }} className="p-1.5 rounded-full bg-white/5 hover:bg-white/10" data-testid={`source-edit-${s.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => del(s.id)} className="p-1.5 rounded-full bg-white/5 hover:bg-red-500/20" data-testid={`source-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-5 text-xs text-gray-400">
        <p className="label-mono mb-2 text-white">Feed Adapter Status</p>
        <div className="grid sm:grid-cols-2 gap-2 mb-4">
          {adapters.map((a) => (
            <div key={a.type} className="flex items-center justify-between p-2.5 rounded bg-white/5" data-testid={`adapter-${a.type}`}>
              <div>
                <div className="text-white text-[12px]">{a.name}</div>
                <div className="text-[10px] text-gray-500">type: {a.type} {a.requires_keys.length > 0 && `· needs: ${a.requires_keys.join(", ")}`}</div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${a.implemented ? "bg-emerald-500/20 text-emerald-400" : "bg-yellow-500/20 text-yellow-400"}`}>
                {a.implemented ? "READY" : "NEEDS KEYS"}
              </span>
            </div>
          ))}
        </div>
        <p className="label-mono mb-2 text-white">CSV Import Format</p>
        <code className="text-[11px] block whitespace-pre-wrap text-gray-300">brand,model,trim,year,image_url,msrp,monthly_payment,due_at_signing,lease_term_months,annual_mileage,expiration_date,region,dealer_source,incentives,vehicle_type,is_luxury,is_ev,featured,status,savings</code>
        <p className="mt-2">Click <b>↓ CSV Template</b> above to download a ready-to-fill file with 10 example brand rows. Imported rows land as <b>pending_verification</b> — approve them in the Lease Deals tab.</p>
      </div>
    </div>
  );
}

function AgreementsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ user_email: "", title: "", body: "", deal_summary: "" });
  const load = () => api.get("/admin/agreements").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      await api.post("/admin/agreements", form);
      toast.success("Agreement created. Customer will be notified.");
      setOpen(false);
      setForm({ user_email: "", title: "", body: "", deal_summary: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full silver-gradient text-[#050507] font-bold text-sm" data-testid="admin-new-agreement">
              <Plus className="w-4 h-4" /> New Agreement
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0A0E] border-white/10 text-white max-w-2xl">
            <DialogHeader><DialogTitle className="font-display">Send Agreement to Customer</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-3">
              <Input placeholder="Customer email (must exist as user)" value={form.user_email} onChange={(e) => setForm({ ...form, user_email: e.target.value })} className="bg-[#050507] border-[#1A1A24] h-10 text-white" data-testid="agreement-form-email" />
              <Input placeholder="Title (e.g. Broker Service Agreement - BMW X5)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#050507] border-[#1A1A24] h-10 text-white" data-testid="agreement-form-title" />
              <Input placeholder="Deal summary (optional)" value={form.deal_summary} onChange={(e) => setForm({ ...form, deal_summary: e.target.value })} className="bg-[#050507] border-[#1A1A24] h-10 text-white" />
              <textarea rows={10} placeholder="Agreement body / terms (will appear on the signed PDF)" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="w-full bg-[#050507] border border-[#1A1A24] rounded-md text-white p-3 text-sm" data-testid="agreement-form-body" />
              <button onClick={save} className="w-full silver-gradient text-[#050507] py-2.5 rounded-full font-bold" data-testid="agreement-form-save">Send to Customer</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-gray-400"><tr><th className="text-left p-3">Title</th><th className="text-left p-3">Customer</th><th className="text-left p-3">Created</th><th className="text-left p-3">Status</th><th className="text-left p-3">Signed</th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-gray-500">No agreements yet</td></tr>}
            {items.map((a) => (
              <tr key={a.id} className="border-t border-white/5" data-testid={`admin-agreement-row-${a.id}`}>
                <td className="p-3"><div className="font-display">{a.title}</div><div className="text-xs text-gray-500">{a.deal_summary}</div></td>
                <td className="p-3 text-gray-300">{a.user_email}</td>
                <td className="p-3 text-gray-500 text-xs">{new Date(a.created_at).toLocaleDateString()}</td>
                <td className="p-3"><span className={`text-[10px] px-2 py-0.5 rounded-full ${a.status === "signed" ? "bg-emerald-500/20 text-emerald-400" : "bg-yellow-500/20 text-yellow-400"}`}>{a.status}</span></td>
                <td className="p-3 text-xs text-gray-400">{a.signed_at ? `${a.signer_name} · ${new Date(a.signed_at).toLocaleString()}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const EMPTY_DEALER = { name: "", location: "", brand_scope: "", transparency: 4, speed: 4, customer_satisfaction: 4, notes: "", visible: true };

function DealersTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_DEALER);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/dealers").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    const body = { ...form, transparency: +form.transparency, speed: +form.speed, customer_satisfaction: +form.customer_satisfaction };
    if (editing) await api.put(`/admin/dealers/${editing}`, body);
    else await api.post("/admin/dealers", body);
    toast.success("Saved"); setOpen(false); load();
  };
  const del = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/admin/dealers/${id}`); load(); };
  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(EMPTY_DEALER); } }}>
          <DialogTrigger asChild>
            <button onClick={() => { setEditing(null); setForm(EMPTY_DEALER); setOpen(true); }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full silver-gradient text-[#050507] font-bold text-sm" data-testid="admin-new-dealer">
              <Plus className="w-4 h-4" /> New Dealer
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0A0E] border-white/10 text-white">
            <DialogHeader><DialogTitle className="font-display">{editing ? "Edit" : "Add"} Dealer</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-3">
              <Input placeholder="Dealer name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#050507] border-[#1A1A24] h-10 text-white" data-testid="dealer-form-name" />
              <Input placeholder="Location (City, State)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-[#050507] border-[#1A1A24] h-10 text-white" data-testid="dealer-form-location" />
              <Input placeholder="Brand scope (BMW, Mercedes…)" value={form.brand_scope} onChange={(e) => setForm({ ...form, brand_scope: e.target.value })} className="bg-[#050507] border-[#1A1A24] h-10 text-white" />
              <div className="grid grid-cols-3 gap-2">
                <Input type="number" min={0} max={5} step={0.1} placeholder="Transparency" value={form.transparency} onChange={(e) => setForm({ ...form, transparency: e.target.value })} className="bg-[#050507] border-[#1A1A24] h-10 text-white" />
                <Input type="number" min={0} max={5} step={0.1} placeholder="Speed" value={form.speed} onChange={(e) => setForm({ ...form, speed: e.target.value })} className="bg-[#050507] border-[#1A1A24] h-10 text-white" />
                <Input type="number" min={0} max={5} step={0.1} placeholder="CSat" value={form.customer_satisfaction} onChange={(e) => setForm({ ...form, customer_satisfaction: e.target.value })} className="bg-[#050507] border-[#1A1A24] h-10 text-white" />
              </div>
              <textarea rows={3} placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-[#050507] border border-[#1A1A24] rounded-md text-white p-3 text-sm" />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} /> Visible publicly</label>
              <button onClick={save} className="w-full silver-gradient text-[#050507] py-2.5 rounded-full font-bold" data-testid="dealer-form-save">{editing ? "Save" : "Create"}</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-gray-400"><tr><th className="text-left p-3">Dealer</th><th className="text-left p-3">Location</th><th className="text-left p-3">Brands</th><th className="text-left p-3">Rating</th><th className="text-left p-3">Visible</th><th className="text-left p-3"></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-gray-500">No dealers yet</td></tr>}
            {items.map((d) => (
              <tr key={d.id} className="border-t border-white/5" data-testid={`admin-dealer-row-${d.id}`}>
                <td className="p-3 font-display">{d.name}</td>
                <td className="p-3 text-gray-400 text-xs">{d.location}</td>
                <td className="p-3 text-gray-400 text-xs">{d.brand_scope}</td>
                <td className="p-3">{d.overall_rating?.toFixed(1)}</td>
                <td className="p-3">{d.visible ? "Yes" : "No"}</td>
                <td className="p-3 flex gap-2">
                  <button onClick={() => { setForm(d); setEditing(d.id); setOpen(true); }} className="p-1.5 rounded-full bg-white/5 hover:bg-white/10" data-testid={`dealer-edit-${d.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => del(d.id)} className="p-1.5 rounded-full bg-white/5 hover:bg-red-500/20" data-testid={`dealer-delete-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmailQueueTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/email-queue").then((r) => setItems(r.data)).catch(() => {}); }, []);
  return (
    <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-white/5 text-gray-400"><tr><th className="text-left p-3">When</th><th className="text-left p-3">Kind</th><th className="text-left p-3">To</th><th className="text-left p-3">Subject</th><th className="text-left p-3">Status</th></tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-gray-500">Email queue is empty. Trigger a booking/lead/agreement and it'll appear here. {`"queued"`} status = pending until RESEND_API_KEY is configured.</td></tr>}
          {items.map((e) => (
            <tr key={e.id} className="border-t border-white/5" data-testid={`email-row-${e.id}`}>
              <td className="p-3 text-gray-500 text-xs">{new Date(e.created_at).toLocaleString()}</td>
              <td className="p-3 label-mono">{e.kind}</td>
              <td className="p-3 text-gray-300 text-xs">{e.to}</td>
              <td className="p-3 text-xs">{e.subject}</td>
              <td className="p-3"><span className={`text-[10px] px-2 py-0.5 rounded-full ${e.status === "sent" ? "bg-emerald-500/20 text-emerald-400" : e.status === "failed" ? "bg-red-500/20 text-red-400" : "bg-yellow-500/20 text-yellow-400"}`}>{e.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PaymentsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/payments").then((r) => setItems(r.data)).catch(() => {}); }, []);
  return (
    <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-white/5 text-gray-400"><tr><th className="text-left p-3">When</th><th className="text-left p-3">Customer</th><th className="text-left p-3">Tier</th><th className="text-left p-3">Amount</th><th className="text-left p-3">Status</th><th className="text-left p-3">Session</th></tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-gray-500">No payment attempts yet</td></tr>}
          {items.map((p) => (
            <tr key={p.id} className="border-t border-white/5" data-testid={`payment-row-${p.id}`}>
              <td className="p-3 text-gray-500 text-xs">{new Date(p.created_at).toLocaleString()}</td>
              <td className="p-3 text-gray-300 text-xs">{p.user_email}</td>
              <td className="p-3 label-mono">{p.tier}</td>
              <td className="p-3">${p.amount}</td>
              <td className="p-3"><span className={`text-[10px] px-2 py-0.5 rounded-full ${p.payment_status === "paid" ? "bg-emerald-500/20 text-emerald-400" : "bg-yellow-500/20 text-yellow-400"}`}>{p.payment_status}</span></td>
              <td className="p-3 text-[10px] text-gray-500 font-mono truncate max-w-xs">{p.session_id}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function OemExtractorDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const extract = async () => {
    if (!url.trim()) return;
    setBusy(true);
    setPreview(null);
    try {
      const { data } = await api.post("/admin/extract-offer", { url: url.trim() });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't fetch / parse this URL");
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      await api.post("/admin/lease-deals/from-extract", {
        brand: preview.brand,
        model: preview.model,
        trim: preview.trim || "",
        year: parseInt(preview.year) || 2025,
        msrp: parseFloat(preview.msrp) || 0,
        monthly_payment: parseFloat(preview.monthly_payment) || 0,
        due_at_signing: parseFloat(preview.due_at_signing) || 0,
        lease_term_months: parseInt(preview.lease_term_months) || 36,
        annual_mileage: parseInt(preview.annual_mileage) || 10000,
        expiration_date: preview.expiration_date || "",
        region: preview.region || "Nationwide",
        incentives: preview.incentives || "",
        image_url: preview.image_url || "",
        vehicle_type: preview.vehicle_type || "sedan",
        is_luxury: !!preview.is_luxury,
        is_ev: !!preview.is_ev,
        disclaimer: preview.disclaimer || "",
        source_url: preview.source_url || url,
        source_name: preview.source_name || preview.brand || "OEM Offer Page",
      });
      toast.success("Saved as Pending Verification — review in the table below");
      setOpen(false);
      setUrl("");
      setPreview(null);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const close = (o) => { setOpen(o); if (!o) { setUrl(""); setPreview(null); } };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogTrigger asChild>
        <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 hover:bg-white/5 text-white text-sm font-bold" data-testid="admin-oem-extract-open">
          <Wand2 className="w-4 h-4" /> OEM URL Extractor
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0E] border-white/10 text-white max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Wand2 className="w-4 h-4 text-silver" />OEM Offer Extractor</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-gray-400 mt-1">Paste any OEM, dealer, or manufacturer offer page URL. AI extracts the headline lease offer and saves it as <b>Pending Verification</b>. You always review before publishing.</p>

        <div className="mt-5 flex gap-2">
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://bmwusa.com/vehicles/x5/offers" className="flex-1 bg-[#050507] border-[#1A1A24] h-11 text-white" data-testid="oem-extract-url" />
          <button onClick={extract} disabled={busy || !url.trim()} className="px-5 rounded-full silver-gradient text-[#050507] font-bold text-sm disabled:opacity-50" data-testid="oem-extract-fetch">
            {busy && !preview ? "Reading…" : "Extract"}
          </button>
        </div>
        <p className="text-[10px] text-gray-500 mt-2 flex items-center gap-1"><LinkIcon className="w-3 h-3" /> Works on bmwusa.com · mbusa.com · audiusa.com · lexus.com · porsche.com · cars.com · and any plain HTML offer page.</p>

        {preview && (
          <div className="mt-6 bg-[#050507] border border-[#1A1A24] rounded-2xl p-5" data-testid="oem-extract-preview">
            <div className="flex items-center justify-between mb-4">
              <div className="label-mono">Preview</div>
              <div className="text-xs text-gray-400">Confidence: <span className={preview.confidence >= 0.7 ? "text-emerald-300" : preview.confidence >= 0.4 ? "text-yellow-300" : "text-red-300"}>{Math.round((preview.confidence || 0) * 100)}%</span></div>
            </div>
            {preview.parse_error && (
              <div className="mb-3 text-xs text-yellow-300 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3" data-testid="oem-preview-error">
                {preview.parse_error}
              </div>
            )}
            {preview.image_warning && (
              <div className="mb-3 text-xs text-orange-300 bg-orange-500/10 border border-orange-500/30 rounded-lg p-3" data-testid="oem-preview-image-warning">
                ⚠ {preview.image_warning}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              {[
                ["brand", "Brand"], ["model", "Model"], ["trim", "Trim"], ["year", "Year"],
                ["msrp", "MSRP"], ["monthly_payment", "Monthly $"], ["due_at_signing", "Due Signing $"],
                ["lease_term_months", "Term (mo)"], ["annual_mileage", "Annual Miles"],
                ["expiration_date", "Expires"], ["region", "Region"], ["vehicle_type", "Type"],
              ].map(([k, l]) => (
                <div key={k}>
                  <label className="label-mono mb-1 block">{l}</label>
                  <Input
                    value={preview[k] ?? ""}
                    onChange={(e) => setPreview({ ...preview, [k]: e.target.value })}
                    className="bg-[#0A0A0E] border-[#1A1A24] h-9 text-white text-sm"
                    data-testid={`oem-preview-${k}`}
                  />
                </div>
              ))}
              <div className="col-span-2">
                <label className="label-mono mb-1 block">Image URL · or upload a photo</label>
                <ImageInput
                  value={preview.image_url || ""}
                  onChange={(v) => setPreview({ ...preview, image_url: v })}
                  testId="oem-preview-image"
                />
              </div>
              <div className="col-span-2">
                <label className="label-mono mb-1 block">Incentives</label>
                <Input value={preview.incentives || ""} onChange={(e) => setPreview({ ...preview, incentives: e.target.value })} className="bg-[#0A0A0E] border-[#1A1A24] h-9 text-white text-sm" />
              </div>
              <div className="col-span-2">
                <label className="label-mono mb-1 block">Disclaimer</label>
                <textarea rows={3} value={preview.disclaimer || ""} onChange={(e) => setPreview({ ...preview, disclaimer: e.target.value })} className="w-full bg-[#0A0A0E] border border-[#1A1A24] rounded-md text-white p-2.5 text-xs" />
              </div>
              <div className="col-span-2 flex gap-3 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!preview.is_luxury} onChange={(e) => setPreview({ ...preview, is_luxury: e.target.checked })} /> Luxury</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!preview.is_ev} onChange={(e) => setPreview({ ...preview, is_ev: e.target.checked })} /> EV</label>
              </div>
            </div>
            {preview.image_url && (
              <div className="mt-4">
                <div className="label-mono mb-2">Vehicle Image</div>
                <img src={preview.image_url} alt="" className="w-full max-h-64 object-cover rounded-xl border border-[#1A1A24]" data-testid="oem-preview-image" />
              </div>
            )}
            <button onClick={save} disabled={busy} className="mt-5 w-full silver-gradient text-[#050507] py-3 rounded-full font-bold disabled:opacity-50" data-testid="oem-extract-save">
              {busy ? "Saving…" : "Save as Pending Verification"}
            </button>
            <p className="text-[10px] text-gray-500 mt-2 text-center">Will land in the table below with status <b>pending_verification</b>. Hit Approve to publish.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Launch Readiness Dashboard
// =============================================================================
function LaunchDashboard({ launch, deals, onReload }) {
  const [alerts, setAlerts] = useState([]);
  const [alertsUnread, setAlertsUnread] = useState(0);

  const loadAlerts = async () => {
    try {
      const [a, u] = await Promise.all([
        api.get("/admin/alerts?limit=20"),
        api.get("/admin/alerts/unread-count"),
      ]);
      setAlerts(a.data); setAlertsUnread(u.data.unread || 0);
    } catch {}
  };
  useEffect(() => { loadAlerts(); }, []);

  const markAllRead = async () => {
    await api.post("/admin/alerts/read-all");
    await loadAlerts();
  };

  if (!launch) {
    return <div className="text-gray-500 text-sm py-12 text-center" data-testid="launch-loading">Loading launch readiness…</div>;
  }
  const pct = launch.launch_progress_pct;
  const pendingDeals = deals.filter((d) => d.verification_status === "pending_verification");

  return (
    <div className="space-y-10" data-testid="launch-dashboard">
      {/* Activity feed — operational alerts from refresh + scheduler */}
      {alerts.length > 0 && (
        <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-6" data-testid="launch-activity-feed">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="label-mono mb-1 flex items-center gap-2"><AlertCircle className="w-3 h-3 text-yellow-300" /> Activity & Alerts</p>
              <p className="text-xs text-gray-500">{alertsUnread} unread · last 20 events</p>
            </div>
            {alertsUnread > 0 && (
              <button onClick={markAllRead} className="text-[10px] text-gray-400 hover:text-white" data-testid="alerts-mark-all-read">Mark all read</button>
            )}
          </div>
          <div className="space-y-2 max-h-[320px] overflow-y-auto">
            {alerts.map((a) => (
              <div
                key={a.id}
                className={`flex items-start gap-3 rounded-xl p-3 border transition ${
                  a.read ? "bg-white/[0.02] border-white/5" :
                  a.level === "error" ? "bg-red-500/10 border-red-500/30" :
                  a.level === "warning" ? "bg-yellow-500/10 border-yellow-500/30" :
                  "bg-emerald-500/[0.05] border-emerald-500/20"
                }`}
                data-testid={`alert-${a.id}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${
                  a.level === "error" ? "bg-red-400" :
                  a.level === "warning" ? "bg-yellow-400" :
                  a.read ? "bg-gray-600" : "bg-emerald-400"
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-sm">{a.title}</div>
                  <div className="text-[11px] text-gray-400 truncate">{a.body}</div>
                  <div className="text-[10px] text-gray-500 mt-1">{new Date(a.created_at).toLocaleString()}{a.source_name ? ` · ${a.source_name}` : ""}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Progress to 50 */}
      <div className="bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/30 rounded-2xl p-7" data-testid="launch-progress-card">
        <div className="flex items-start justify-between mb-5 flex-wrap gap-4">
          <div>
            <p className="label-mono mb-2 flex items-center gap-2"><Target className="w-3 h-3 text-emerald-400" /> Launch Target</p>
            <div className="font-display text-3xl mb-1">{launch.verified_count} <span className="text-gray-500 text-xl">/ {launch.launch_target}</span> verified deals</div>
            <p className="text-xs text-gray-400">{pct >= 100 ? "Marketplace is launch-ready." : `${(launch.launch_target - launch.verified_count)} more verified deals to hit launch threshold.`}</p>
          </div>
          <div className="text-right">
            <div className="text-[10px] text-gray-500 tracking-widest uppercase">Pending Review</div>
            <div className="font-display text-3xl text-yellow-300">{launch.pending_count}</div>
            <div className="text-[10px] text-emerald-300 mt-1">{launch.ready_to_publish_now} ready to publish now</div>
          </div>
        </div>
        <div className="h-3 bg-white/5 rounded-full overflow-hidden">
          <div className={`h-full transition-all ${pct >= 100 ? "bg-emerald-400" : pct >= 50 ? "silver-gradient" : "bg-yellow-400"}`} style={{ width: `${pct}%` }} data-testid="launch-progress-bar" />
        </div>
        <div className="mt-3 text-[10px] text-gray-500 flex justify-between"><span>0</span><span>{launch.launch_target}</span></div>
      </div>

      {/* Key pipeline KPIs */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <LaunchKpi label="Leads (total)" value={launch.leads_total} testId="launch-kpi-leads" />
        <LaunchKpi label="Saved Searches" value={launch.saved_searches_total} testId="launch-kpi-saved" />
        <LaunchKpi label="Exotic Requests" value={launch.exotic_requests_total} testId="launch-kpi-exotic" />
        <LaunchKpi label="AI Chats" value={launch.chats_total} testId="launch-kpi-chats" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Most viewed */}
        <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-6" data-testid="launch-most-viewed">
          <div className="flex items-center justify-between mb-4">
            <p className="label-mono">Most Viewed Vehicles</p>
            <span className="text-[10px] text-gray-500">verified · by clicks</span>
          </div>
          {launch.most_viewed.length === 0 ? (
            <p className="text-xs text-gray-500 py-6 text-center">No verified deals yet — publish your pending pipeline.</p>
          ) : (
            <ul className="space-y-3">
              {launch.most_viewed.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-white">{d.brand} {d.model} <span className="text-gray-500 text-xs">{d.trim}</span></span>
                  <span className="text-gray-400 font-mono text-xs">{d.clicks || 0} clicks</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Most requested */}
        <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-6" data-testid="launch-most-requested">
          <div className="flex items-center justify-between mb-4">
            <p className="label-mono">Most Requested Vehicles</p>
            <span className="text-[10px] text-gray-500">from leads + exotic requests</span>
          </div>
          {launch.most_requested.length === 0 ? (
            <p className="text-xs text-gray-500 py-6 text-center">No demand signal yet.</p>
          ) : (
            <ul className="space-y-3">
              {launch.most_requested.map((r, i) => (
                <li key={i} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-white capitalize">{r.vehicle}</span>
                  <span className="text-gray-400 font-mono text-xs">{r.count} request{r.count > 1 ? "s" : ""}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Top brands */}
        <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-6" data-testid="launch-top-brands">
          <div className="flex items-center justify-between mb-4">
            <p className="label-mono">Top-Performing Brands</p>
            <span className="text-[10px] text-gray-500">verified deal count · clicks</span>
          </div>
          {launch.top_brands.length === 0 ? (
            <p className="text-xs text-gray-500 py-6 text-center">Publish verified deals to populate this leaderboard.</p>
          ) : (
            <ul className="space-y-3">
              {launch.top_brands.map((b) => (
                <li key={b.brand} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-white">{b.brand}</span>
                  <span className="text-gray-400 font-mono text-xs">{b.deals} deals · {b.clicks} clicks</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Top deals */}
        <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-6" data-testid="launch-top-deals">
          <div className="flex items-center justify-between mb-4">
            <p className="label-mono">Top-Performing Lease Deals</p>
            <span className="text-[10px] text-gray-500">verified · by clicks</span>
          </div>
          {launch.top_deals.length === 0 ? (
            <p className="text-xs text-gray-500 py-6 text-center">—</p>
          ) : (
            <ul className="space-y-3">
              {launch.top_deals.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-white">{d.brand} {d.model} <span className="text-gray-500 text-xs">${d.monthly_payment}/mo</span></span>
                  <span className="text-gray-400 font-mono text-xs">{d.clicks || 0} clicks</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Pending pre-publish checklist */}
      {pendingDeals.length > 0 && (
        <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-6" data-testid="launch-pending-checklist">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
            <div>
              <p className="label-mono mb-2 flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Pre-Publish Checklist</p>
              <p className="text-xs text-gray-400">{pendingDeals.length} pending · {launch.ready_to_publish_now} ready to publish now · {launch.pending_needs_image} need image · {launch.pending_needs_intel} need scoring</p>
            </div>
          </div>
          <div className="grid gap-3">
            {pendingDeals.slice(0, 12).map((d) => <DealChecklistRow key={d.id} deal={d} onApproved={onReload} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function LaunchKpi({ label, value, testId }) {
  return (
    <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-5" data-testid={testId}>
      <div className="label-mono mb-2">{label}</div>
      <div className="font-display text-3xl">{value}</div>
    </div>
  );
}

function DealChecklistRow({ deal, onApproved }) {
  const [check, setCheck] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadCheck = async () => {
    const { data } = await api.get(`/admin/lease-deals/${deal.id}/checklist`);
    setCheck(data);
  };

  useEffect(() => { loadCheck(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [deal.id, deal.image_url, deal.intelligence_score, deal.expiration_date]);

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/lease-deals/${deal.id}/approve`, { verification_status: "oem_verified" });
      toast.success(`Published ${deal.brand} ${deal.model}`);
      onApproved?.();
    } catch { toast.error("Approve failed"); }
    finally { setBusy(false); }
  };

  const scoreIntel = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/lease-deals/${deal.id}/score-intelligence`);
      toast.success("Scored");
      await loadCheck();
      onApproved?.();
    } catch { toast.error("Score failed"); }
    finally { setBusy(false); }
  };

  if (!check) return <div className="text-xs text-gray-500 py-2">Loading checklist…</div>;
  const ready = check.ready_to_publish;

  return (
    <div className={`border rounded-xl p-4 transition ${ready ? "border-emerald-500/30 bg-emerald-500/[0.03]" : "border-yellow-500/20 bg-yellow-500/[0.02]"}`} data-testid={`checklist-row-${deal.id}`}>
      <div className="flex items-center justify-between gap-3 cursor-pointer" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {deal.image_url ? (
            <img src={deal.image_url} alt="" className="w-12 h-12 rounded-md object-cover bg-white/5" />
          ) : (
            <div className="w-12 h-12 rounded-md bg-white/5 flex items-center justify-center text-[9px] text-gray-500 tracking-wider">NO IMG</div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-display text-sm truncate">{deal.brand} {deal.model} <span className="text-gray-500 text-xs">{deal.trim}</span></div>
            <div className="text-[11px] text-gray-400">${deal.monthly_payment}/mo · DAS ${deal.due_at_signing} · {deal.lease_term_months}mo</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-[10px] font-bold tracking-widest px-2 py-1 rounded-full ${ready ? "bg-emerald-500/20 text-emerald-300" : "bg-yellow-500/20 text-yellow-300"}`}>
            {check.passed}/{check.total} {ready ? "READY" : "PENDING"}
          </span>
          {ready && (
            <button
              onClick={(e) => { e.stopPropagation(); approve(); }}
              disabled={busy}
              className="px-3 py-1.5 rounded-full silver-gradient text-[#050507] text-[11px] font-bold disabled:opacity-50"
              data-testid={`checklist-publish-${deal.id}`}
            >
              {busy ? "…" : "Publish"}
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="mt-4 pt-4 border-t border-white/5 grid sm:grid-cols-2 gap-2 text-xs" data-testid={`checklist-detail-${deal.id}`}>
          {Object.entries(check.items).map(([key, c]) => (
            <div key={key} className="flex items-start gap-2">
              {c.pass ? <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />}
              <span className={c.pass ? "text-gray-300" : "text-yellow-200"}>{c.label}</span>
            </div>
          ))}
          {!check.items.deal_score.pass && (
            <button onClick={(e) => { e.stopPropagation(); scoreIntel(); }} disabled={busy} className="col-span-2 mt-2 px-3 py-1.5 rounded-full bg-violet-500/20 hover:bg-violet-500/30 text-violet-300 text-[11px] font-bold inline-flex items-center justify-center gap-1 disabled:opacity-50" data-testid={`checklist-score-${deal.id}`}>
              <Sparkles className="w-3 h-3" /> Score deal intelligence
            </button>
          )}
          <a href={deal.source_url || "#"} target="_blank" rel="noreferrer" className="col-span-2 mt-1 text-[10px] text-gray-500 hover:text-gray-300 truncate">
            Source: {deal.source_url || "—"}
          </a>
        </div>
      )}
    </div>
  );
}


// =============================================================================
// Source Library Tab — pre-approved sources with auto-publish
// =============================================================================
const EMPTY_DEALER_SOURCE = { name: "", brand: "", state: "US", url: "", source_type: "Dealer", refresh_frequency: "24h", trusted: true };

function SourceLibraryTab() {
  const [sources, setSources] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_DEALER_SOURCE);
  const [editingId, setEditingId] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [refreshing, setRefreshing] = useState(null);
  const [job, setJob] = useState(null);     // { id, total, completed, status, results }
  const [lastRun, setLastRun] = useState(null);

  const load = () => api.get("/admin/dealer-sources").then((r) => setSources(r.data));
  useEffect(() => { load(); }, []);

  // Poll active refresh job
  useEffect(() => {
    if (!job?.id || job.status === "done" || job.status === "cancelled" || job.status === "error") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/refresh-jobs/${job.id}`);
        setJob(data);
        if (data.status === "done") {
          setLastRun(data.summary);
          await load();
        }
      } catch {}
    }, 2500);
    return () => clearInterval(t);
  }, [job?.id, job?.status]);

  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/dealer-sources/seed-approved");
      toast.success(`Seeded ${data.inserted} approved sources (${data.skipped_existing} already existed)`);
      await load();
    } catch { toast.error("Seed failed"); }
    finally { setSeeding(false); }
  };

  const save = async () => {
    try {
      if (editingId) await api.put(`/admin/dealer-sources/${editingId}`, form);
      else await api.post("/admin/dealer-sources", form);
      toast.success(editingId ? "Source updated" : "Source added");
      setOpen(false);
      await load();
    } catch { toast.error("Save failed"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this source? Deals published from it will remain.")) return;
    await api.delete(`/admin/dealer-sources/${id}`);
    toast.success("Source deleted");
    await load();
  };

  const refresh = async (id) => {
    setRefreshing(id);
    try {
      const { data } = await api.post(`/admin/dealer-sources/${id}/refresh`);
      if (data.auto_published) {
        toast.success(`Published as ${data.verification_status?.replace("_", " ")}`);
      } else {
        toast.message("Saved to Pending", { description: data.reason || "" });
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally { setRefreshing(null); }
  };

  const refreshAll = async () => {
    if (!confirm(`Queue background refresh for all ${sources.length} trusted sources?\nThe job will run while you keep working. Watch progress in the banner.`)) return;
    setLastRun(null);
    try {
      const { data } = await api.post("/admin/dealer-sources/refresh-all-job");
      setJob({ id: data.job_id, total: data.total, completed: 0, status: "queued", results: [] });
      toast.success(`Job queued — ${data.total} sources`);
    } catch {
      toast.error("Couldn't queue refresh job");
    }
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({ name: s.name, brand: s.brand, state: s.state, url: s.url, source_type: s.source_type, refresh_frequency: s.refresh_frequency, trusted: !!s.trusted });
    setOpen(true);
  };

  const grouped = sources.reduce((acc, s) => {
    acc[s.source_type] = acc[s.source_type] || [];
    acc[s.source_type].push(s);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="source-library">
      {/* Header strip */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="label-mono mb-1">Approved Sources</p>
          <p className="text-xs text-gray-500">{sources.length} sources · {sources.filter((s) => s.trusted).length} trusted (auto-publish) · {sources.reduce((sum, s) => sum + (s.deals_published || 0), 0)} deals published</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={seed} disabled={seeding} className="px-4 py-2 rounded-full border border-emerald-500/40 text-emerald-300 text-xs hover:bg-emerald-500/10 disabled:opacity-50" data-testid="source-seed">
            {seeding ? "Seeding…" : "Seed 30 Approved"}
          </button>
          <button onClick={refreshAll} disabled={job && job.status !== "done"} className="px-4 py-2 rounded-full border border-violet-500/40 text-violet-300 text-xs hover:bg-violet-500/10 disabled:opacity-50" data-testid="source-refresh-all">
            {job && job.status !== "done" ? `Refreshing… ${job.completed}/${job.total}` : "↻ Refresh All (background)"}
          </button>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm(EMPTY_DEALER_SOURCE); } }}>
            <DialogTrigger asChild>
              <button onClick={() => { setEditingId(null); setForm(EMPTY_DEALER_SOURCE); setOpen(true); }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full silver-gradient text-[#050507] text-xs font-bold" data-testid="source-add">
                <Plus className="w-3 h-3" /> Add Source
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0E] border-white/10 text-white max-w-lg">
              <DialogHeader><DialogTitle className="font-display">{editingId ? "Edit Source" : "Add Source"}</DialogTitle><DialogDescription className="text-xs text-gray-500">Trusted sources auto-publish extracted deals; otherwise they land in Pending Verification.</DialogDescription></DialogHeader>
              <div className="space-y-3 mt-4">
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Dealer/OEM name" className="bg-[#050507] border-[#1A1A24] h-10 text-white" data-testid="source-form-name" />
                <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Brand (e.g. BMW)" className="bg-[#050507] border-[#1A1A24] h-10 text-white" data-testid="source-form-brand" />
                <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://… source URL" className="bg-[#050507] border-[#1A1A24] h-10 text-white" data-testid="source-form-url" />
                <div className="grid grid-cols-2 gap-3">
                  <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="State (NC/US)" maxLength={3} className="bg-[#050507] border-[#1A1A24] h-10 text-white" data-testid="source-form-state" />
                  <select value={form.source_type} onChange={(e) => setForm({ ...form, source_type: e.target.value })} className="bg-[#050507] border border-[#1A1A24] h-10 rounded-md px-2 text-sm text-white" data-testid="source-form-type">
                    <option>OEM</option><option>Dealer</option><option>Exotic Dealer</option><option>Aggregator</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <select value={form.refresh_frequency} onChange={(e) => setForm({ ...form, refresh_frequency: e.target.value })} className="bg-[#050507] border border-[#1A1A24] h-10 rounded-md px-2 text-sm text-white" data-testid="source-form-freq">
                    <option value="12h">Every 12 hours</option><option value="24h">Every 24 hours</option><option value="weekly">Weekly</option><option value="manual">Manual only</option>
                  </select>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.trusted} onChange={(e) => setForm({ ...form, trusted: e.target.checked })} data-testid="source-form-trusted" />
                    Trusted (auto-publish)
                  </label>
                </div>
                <button onClick={save} className="w-full silver-gradient text-[#050507] py-2.5 rounded-full font-bold mt-3" data-testid="source-form-save">Save Source</button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Live refresh job progress */}
      {job && job.status !== "done" && (
        <div className="bg-violet-500/10 border border-violet-500/30 rounded-xl p-4" data-testid="source-job-progress">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-display text-sm">Background refresh running…</div>
              <p className="text-[11px] text-violet-200">Status: {job.status} · {job.completed}/{job.total} sources processed</p>
            </div>
            <div className="text-right">
              <div className="font-display text-xl text-violet-200">{Math.round((job.completed / Math.max(job.total, 1)) * 100)}%</div>
            </div>
          </div>
          <div className="h-2 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-violet-400 transition-all" style={{ width: `${(job.completed / Math.max(job.total, 1)) * 100}%` }} />
          </div>
        </div>
      )}

      {/* Completed run summary */}
      {lastRun && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-sm text-emerald-100" data-testid="source-last-run">
          <b>Last refresh:</b> {lastRun.auto_published} auto-published · {lastRun.pending} pending · {lastRun.errors} errors (out of {lastRun.total})
        </div>
      )}

      {sources.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-white/10 rounded-2xl">
          <p className="text-gray-400 mb-5">No sources yet. Seed the 30 approved sources to get started.</p>
          <button onClick={seed} className="px-6 py-3 rounded-full silver-gradient text-[#050507] font-bold text-sm">Seed 30 Approved Sources</button>
        </div>
      ) : (
        Object.entries(grouped).map(([type, list]) => (
          <div key={type} className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl overflow-hidden" data-testid={`source-group-${type.toLowerCase().replace(/ /g, "-")}`}>
            <div className="px-5 py-3 bg-white/5 flex items-center justify-between">
              <div className="label-mono">{type}</div>
              <div className="text-[10px] text-gray-500">{list.length} sources</div>
            </div>
            <table className="w-full text-sm">
              <thead className="text-gray-500 border-b border-white/5">
                <tr><th className="text-left p-3 font-normal text-[10px] tracking-widest">Brand</th><th className="text-left p-3 font-normal text-[10px] tracking-widest">Name</th><th className="text-left p-3 font-normal text-[10px] tracking-widest">State</th><th className="text-left p-3 font-normal text-[10px] tracking-widest">Last Refresh</th><th className="text-left p-3 font-normal text-[10px] tracking-widest">Published</th><th className="text-left p-3 font-normal text-[10px] tracking-widest">Trust</th><th /></tr>
              </thead>
              <tbody>
                {list.map((s) => (
                  <tr key={s.id} className="border-t border-white/5" data-testid={`source-row-${s.id}`}>
                    <td className="p-3 font-display">{s.brand}</td>
                    <td className="p-3"><a href={s.url} target="_blank" rel="noreferrer" className="hover:text-white text-gray-300 truncate inline-block max-w-[240px]" title={s.url}>{s.name}</a></td>
                    <td className="p-3 text-xs text-gray-400">{s.state}</td>
                    <td className="p-3 text-xs text-gray-400">{s.last_refresh_at ? new Date(s.last_refresh_at).toLocaleString() : <span className="text-yellow-300">Never</span>}</td>
                    <td className="p-3 text-xs text-gray-400">{s.deals_published || 0}</td>
                    <td className="p-3"><span className={`text-[10px] px-2 py-0.5 rounded-full ${s.trusted ? "bg-emerald-500/20 text-emerald-300" : "bg-gray-500/20 text-gray-300"}`}>{s.trusted ? "AUTO" : "MANUAL"}</span></td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        <button onClick={() => refresh(s.id)} disabled={refreshing === s.id || (job && job.status !== "done")} className="px-2 py-1 rounded bg-violet-500/20 hover:bg-violet-500/30 text-violet-300 text-[10px] disabled:opacity-50" data-testid={`source-refresh-${s.id}`}>
                          {refreshing === s.id ? "…" : "↻ Refresh"}
                        </button>
                        <button onClick={() => openEdit(s)} className="p-1.5 rounded-full bg-white/5 hover:bg-white/10" data-testid={`source-edit-${s.id}`}><Pencil className="w-3 h-3" /></button>
                        <button onClick={() => del(s.id)} className="p-1.5 rounded-full bg-white/5 hover:bg-red-500/20" data-testid={`source-delete-${s.id}`}><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}

      <p className="text-[11px] text-gray-500">
        <b>Auto-publish rules:</b> Trusted sources publish immediately when extraction returns confidence ≥ 0.6, valid brand/model, monthly payment &gt; $50, and an image whose URL contains the brand or model. Otherwise the deal lands in Pending Verification. Subsequent refreshes update the same deal in place; payment changes &gt; 10% flip it to <b>Needs Review</b> while keeping it live.
      </p>
    </div>
  );
}


// =============================================================================
// Quick Entry — one mini-form per approved source, ~30s per deal
// =============================================================================
function QuickEntryTab() {
  const [sources, setSources] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/admin/dealer-sources").then((r) => setSources(r.data));
  }, []);

  const filtered = sources.filter((s) => {
    if (filter !== "all" && s.source_type !== filter) return false;
    if (search && !`${s.brand} ${s.name}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="quick-entry-tab">
      <div className="bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/30 rounded-2xl p-6">
        <p className="label-mono mb-2 flex items-center gap-2"><Plus className="w-3 h-3 text-emerald-400" /> Quick Entry — 30 second per deal</p>
        <h3 className="font-display text-xl mb-2">Type one verified offer per approved source</h3>
        <p className="text-xs text-gray-400 leading-relaxed max-w-2xl">
          Open the source URL in your browser, grab the headline lease, paste the 6 fields below, hit Publish. Brand, source URL, and verification badge are pre-filled. Image-confidence is still validated — paste a vehicle photo URL, not a logo.
        </p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search brand / dealer…"
          className="bg-[#0A0A0E] border-[#1A1A24] h-10 text-white max-w-xs"
          data-testid="quick-search"
        />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-[#0A0A0E] border border-[#1A1A24] h-10 rounded-md px-3 text-sm text-white" data-testid="quick-filter">
          <option value="all">All types ({sources.length})</option>
          <option>OEM</option><option>Dealer</option><option>Exotic Dealer</option><option>Aggregator</option>
        </select>
        <div className="ml-auto text-[11px] text-gray-500">
          Showing {filtered.length} of {sources.length} sources
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {filtered.map((s) => <QuickEntryCard key={s.id} source={s} />)}
      </div>

      {filtered.length === 0 && (
        <div className="border border-dashed border-white/10 rounded-2xl py-16 text-center text-gray-500">
          No sources match your filter.
        </div>
      )}
    </div>
  );
}

function QuickEntryCard({ source }) {
  const [form, setForm] = useState({
    model: "", trim: "", year: 2025, msrp: "",
    monthly_payment: "", due_at_signing: "", lease_term_months: 36, annual_mileage: 10000,
    image_url: "", expiration_date: "", incentives: "",
    vehicle_type: "sedan", is_luxury: source.source_type === "Exotic Dealer" || ["BMW", "Mercedes-Benz", "Audi", "Lexus", "Porsche", "Genesis", "Cadillac", "Lincoln", "Land Rover", "Maserati"].includes(source.brand),
    is_ev: source.brand === "Tesla",
  });
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const verifyBadge = source.source_type === "OEM" ? "OEM-Verified" : source.source_type === "Aggregator" ? "Admin-Verified" : "Dealer-Verified";
  const badgeColor = source.source_type === "OEM" ? "bg-emerald-500/20 text-emerald-300 border-emerald-400/40"
                   : source.source_type === "Exotic Dealer" ? "bg-yellow-500/20 text-yellow-300 border-yellow-400/40"
                   : source.source_type === "Aggregator" ? "bg-violet-500/20 text-violet-300 border-violet-400/40"
                   : "bg-blue-500/20 text-blue-300 border-blue-400/40";

  const required = form.model && form.monthly_payment && form.due_at_signing && form.image_url;

  const submit = async () => {
    if (!required) { toast.error("Need model, monthly, DAS, and image URL"); return; }
    setBusy(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/admin/lease-deals/quick-add", {
        source_id: source.id,
        model: form.model,
        trim: form.trim,
        year: parseInt(form.year) || 2025,
        msrp: parseFloat(form.msrp) || 0,
        monthly_payment: parseFloat(form.monthly_payment),
        due_at_signing: parseFloat(form.due_at_signing),
        lease_term_months: parseInt(form.lease_term_months) || 36,
        annual_mileage: parseInt(form.annual_mileage) || 10000,
        image_url: form.image_url.trim(),
        expiration_date: form.expiration_date,
        incentives: form.incentives,
        vehicle_type: form.vehicle_type,
        is_luxury: form.is_luxury,
        is_ev: form.is_ev,
      });
      setLastResult(data);
      if (data.verification_status === "needs_review") {
        toast.warning(`Saved to needs_review · image confidence ${data.image_confidence.toFixed(2)}: ${data.image_reason}`);
      } else {
        toast.success(`Published ${source.brand} ${form.model} as ${data.verification_status.replace("_", " ")}`);
      }
      // Reset for next deal
      setForm({ ...form, model: "", trim: "", msrp: "", monthly_payment: "", due_at_signing: "", image_url: "", expiration_date: "", incentives: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-5" data-testid={`quick-card-${source.id}`}>
      {/* Header: brand + source + badge */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg flex items-center gap-2">
            {source.brand}
            <span className={`text-[9px] px-2 py-0.5 rounded-full border ${badgeColor} font-bold tracking-widest`}>
              {verifyBadge}
            </span>
          </div>
          <a href={source.url} target="_blank" rel="noreferrer" className="text-xs text-gray-400 hover:text-white truncate inline-block max-w-full" title={source.url}>
            ↗ {source.name}
          </a>
          <div className="text-[10px] text-gray-500 mt-0.5">{source.state} · {source.source_type}{source.deals_published ? ` · ${source.deals_published} published` : ""}</div>
        </div>
      </div>

      {/* Compact 3x2 grid of essential fields */}
      <div className="grid grid-cols-2 gap-2 mb-2">
        <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="Model *" className="bg-[#050507] border-[#1A1A24] h-9 text-white text-sm" data-testid={`q-model-${source.id}`} />
        <Input value={form.trim} onChange={(e) => setForm({ ...form, trim: e.target.value })} placeholder="Trim" className="bg-[#050507] border-[#1A1A24] h-9 text-white text-sm" data-testid={`q-trim-${source.id}`} />
        <Input type="number" value={form.monthly_payment} onChange={(e) => setForm({ ...form, monthly_payment: e.target.value })} placeholder="Monthly $ *" className="bg-[#050507] border-[#1A1A24] h-9 text-white text-sm" data-testid={`q-monthly-${source.id}`} />
        <Input type="number" value={form.due_at_signing} onChange={(e) => setForm({ ...form, due_at_signing: e.target.value })} placeholder="Due at signing $ *" className="bg-[#050507] border-[#1A1A24] h-9 text-white text-sm" data-testid={`q-das-${source.id}`} />
        <Input type="number" value={form.lease_term_months} onChange={(e) => setForm({ ...form, lease_term_months: e.target.value })} placeholder="Term mo" className="bg-[#050507] border-[#1A1A24] h-9 text-white text-sm" data-testid={`q-term-${source.id}`} />
        <Input type="number" value={form.msrp} onChange={(e) => setForm({ ...form, msrp: e.target.value })} placeholder="MSRP $" className="bg-[#050507] border-[#1A1A24] h-9 text-white text-sm" data-testid={`q-msrp-${source.id}`} />
      </div>
      <div className="mb-2">
        <ImageInput
          value={form.image_url}
          onChange={(v) => setForm({ ...form, image_url: v })}
          testId={`q-image-${source.id}`}
          placeholder="Vehicle image — paste URL or upload photo *"
          compact
        />
      </div>

      {/* Collapsible optional row */}
      <details className="mb-3">
        <summary className="text-[10px] text-gray-500 cursor-pointer hover:text-gray-300">More fields (year, miles, expiration, type)</summary>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="Year" className="bg-[#050507] border-[#1A1A24] h-9 text-white text-sm" />
          <Input type="number" value={form.annual_mileage} onChange={(e) => setForm({ ...form, annual_mileage: e.target.value })} placeholder="Annual miles" className="bg-[#050507] border-[#1A1A24] h-9 text-white text-sm" />
          <Input value={form.expiration_date} onChange={(e) => setForm({ ...form, expiration_date: e.target.value })} placeholder="Expiration YYYY-MM-DD" className="bg-[#050507] border-[#1A1A24] h-9 text-white text-sm" />
          <select value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })} className="bg-[#050507] border border-[#1A1A24] h-9 rounded-md px-2 text-sm text-white">
            <option>sedan</option><option>suv</option><option>truck</option><option>coupe</option><option>ev</option>
          </select>
          <Input value={form.incentives} onChange={(e) => setForm({ ...form, incentives: e.target.value })} placeholder="Incentives (optional)" className="bg-[#050507] border-[#1A1A24] h-9 text-white text-sm col-span-2" />
          <label className="flex items-center gap-2 text-xs text-gray-400"><input type="checkbox" checked={form.is_luxury} onChange={(e) => setForm({ ...form, is_luxury: e.target.checked })} /> Luxury</label>
          <label className="flex items-center gap-2 text-xs text-gray-400"><input type="checkbox" checked={form.is_ev} onChange={(e) => setForm({ ...form, is_ev: e.target.checked })} /> Electric</label>
        </div>
      </details>

      <button
        onClick={submit}
        disabled={!required || busy}
        className={`w-full py-2.5 rounded-full font-bold text-sm disabled:opacity-40 ${required ? "silver-gradient text-[#050507]" : "bg-white/5 text-gray-500"}`}
        data-testid={`q-submit-${source.id}`}
      >
        {busy ? "Saving…" : `Publish as ${verifyBadge}`}
      </button>

      {lastResult && (
        <div className={`mt-3 text-[11px] p-2 rounded-md ${lastResult.verification_status === "needs_review" ? "bg-yellow-500/10 text-yellow-300" : "bg-emerald-500/10 text-emerald-300"}`}>
          ✓ {lastResult.deal_id?.slice(0, 14)}… · {lastResult.verification_status} · img_conf {lastResult.image_confidence?.toFixed(2)}
        </div>
      )}
    </div>
  );
}


// =============================================================================
function ImportUrlDialog({ open, onClose, onDone }) {
  const EMPTY = { year: 2024, make: "", model: "", trim: "", price: "", mileage: "", vin: "", condition: "used", dealer_name: "", location: "", image_url: "", source_url: "" };
  const [url, setUrl] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [meta, setMeta] = useState(null); // {parse_error, image_warning, confidence}

  const reset = () => { setUrl(""); setForm(EMPTY); setMeta(null); };

  const extract = async () => {
    if (!url.trim()) return;
    setExtracting(true); setMeta(null);
    try {
      const { data } = await api.post("/admin/authorized/extract-url", { url: url.trim() });
      setForm({
        year: data.year || 2024, make: data.make || "", model: data.model || "", trim: data.trim || "",
        price: data.price || "", mileage: data.mileage || "", vin: data.vin || "",
        condition: data.condition || "used", dealer_name: data.dealer_name || "", location: data.location || "",
        image_url: data.image_url || "", source_url: data.source_url || url.trim(),
      });
      setMeta({ parse_error: data.parse_error, image_warning: data.image_warning, confidence: data.confidence });
      if (data.parse_error) toast.warning("Partial extract — review the fields below");
      else toast.success("Extracted — review and save");
    } catch { toast.error("Extraction failed — fill fields manually"); }
    finally { setExtracting(false); }
  };

  const save = async () => {
    if (!form.make || !form.model || !form.price || Number(form.price) <= 50) {
      toast.error("Make, model and a real price are required"); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, year: Number(form.year), price: Number(form.price), mileage: Number(form.mileage || 0) };
      const { data } = await api.post("/admin/authorized/import", payload);
      toast.success("Added to Import Review — publish it from the Import Review tab", { id: "import-save" });
      reset(); onClose(); onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const field = (k, label, type = "text") => (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">{label}</label>
      <input
        type={type} value={form[k]}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        className="w-full bg-[#050507] border border-[#1A1A24] h-9 rounded-md text-white px-3 text-sm"
        data-testid={`import-field-${k}`}
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="bg-[#0A0A0E] border-[#1A1A24] text-white max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="import-url-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-light">Import a vehicle from any listing URL</DialogTitle>
          <DialogDescription className="text-gray-400 text-sm">
            Paste a CarGurus, Autotrader, Cars.com or dealer listing link. We render the page, pull the price, photo and details, then you review before it goes live on your inventory.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 mb-4">
          <Input
            placeholder="https://www.cargurus.com/…"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="bg-[#050507] border-[#1A1A24] text-white h-10 rounded-md flex-1"
            data-testid="import-url-input"
          />
          <button
            onClick={extract} disabled={extracting || !url.trim()}
            className="px-5 rounded-md silver-gradient text-[#050507] font-bold text-sm disabled:opacity-50 inline-flex items-center gap-2 whitespace-nowrap"
            data-testid="import-extract-btn"
          >
            {extracting ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Reading…</> : <><Wand2 className="w-3.5 h-3.5" /> Extract</>}
          </button>
        </div>

        {meta?.parse_error && (
          <div className="mb-3 p-3 rounded-lg bg-yellow-500/5 border border-yellow-500/20 text-xs text-yellow-200">{meta.parse_error}</div>
        )}
        {meta?.image_warning && (
          <div className="mb-3 p-3 rounded-lg bg-yellow-500/5 border border-yellow-500/20 text-xs text-yellow-200">{meta.image_warning}</div>
        )}

        <div className="grid grid-cols-2 gap-3">
          {field("year", "Year", "number")}
          {field("make", "Make")}
          {field("model", "Model")}
          {field("trim", "Trim")}
          {field("price", "Price ($)", "number")}
          {field("mileage", "Mileage", "number")}
          {field("vin", "VIN")}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">Condition</label>
            <select value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className="w-full bg-[#050507] border border-[#1A1A24] h-9 rounded-md text-white px-3 text-sm" data-testid="import-field-condition">
              <option value="new">New</option>
              <option value="cpo">CPO</option>
              <option value="used">Used</option>
            </select>
          </div>
          {field("dealer_name", "Dealer / Source")}
          {field("location", "Location")}
        </div>
        <div className="mt-3">
          {field("image_url", "Image URL")}
          {form.image_url && (
            <div className="mt-2 aspect-[16/9] max-w-xs rounded-lg overflow-hidden border border-white/10 bg-[#050507]">
              <img src={form.image_url} alt="preview" className="w-full h-full object-cover" data-testid="import-image-preview" />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={() => { reset(); onClose(); }} className="px-5 py-2.5 rounded-full border border-white/15 text-sm hover:bg-white/5">Cancel</button>
          <button onClick={save} disabled={saving} className="px-6 py-2.5 rounded-full silver-gradient text-[#050507] font-bold text-sm disabled:opacity-50 inline-flex items-center gap-2" data-testid="import-save-btn">
            {saving ? "Adding…" : <><Check className="w-4 h-4" /> Add to Inventory</>}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// Authorized Inventory Feed (P0 — mocked feed stub)
// =============================================================================
function BulkImportDialog({ open, onClose, onDone }) {
  const [text, setText] = useState("");
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const urls = text.split("\n").map((u) => u.trim()).filter(Boolean);

  const start = async () => {
    if (urls.length === 0) { toast.error("Paste at least one URL"); return; }
    setStarting(true); setJob(null);
    try {
      const { data } = await api.post("/admin/authorized/bulk-import-job", { urls });
      const poll = async () => {
        try {
          const { data: j } = await api.get(`/admin/authorized/import-jobs/${data.job_id}`);
          setJob(j);
          if (j.status === "done") { clearInterval(pollRef.current); pollRef.current = null; onDone && onDone(); }
        } catch {}
      };
      await poll();
      pollRef.current = setInterval(poll, 2500);
    } catch { toast.error("Couldn't start import"); }
    finally { setStarting(false); }
  };

  const reset = () => { setText(""); setJob(null); if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const STATUS_STYLE = {
    imported: "bg-emerald-500/15 text-emerald-300",
    needs_review: "bg-blue-500/15 text-blue-300",
    duplicate: "bg-yellow-500/15 text-yellow-300",
    source_blocked: "bg-orange-500/15 text-orange-300",
    failed: "bg-red-500/15 text-red-300",
  };
  const running = job && job.status !== "done";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="bg-[#0A0A0E] border-[#1A1A24] text-white max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-light">Bulk import vehicle listings</DialogTitle>
          <DialogDescription className="text-gray-400 text-sm">
            Paste one listing URL per line (up to 20). We render each page, extract the vehicle, dedupe by VIN then URL, and drop the results into Import Review. Nothing is published automatically.
          </DialogDescription>
        </DialogHeader>

        {!job && (
          <>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={8}
              placeholder={"https://www.cargurus.com/listing/123\nhttps://www.autotrader.com/cars-for-sale/vehicle/456\nhttps://dealer.com/inventory/789"}
              className="w-full bg-[#050507] border border-[#1A1A24] rounded-lg text-white p-3 text-sm font-mono"
              data-testid="bulk-import-textarea"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-500">{urls.length} URL{urls.length !== 1 ? "s" : ""} {urls.length > 20 && <span className="text-yellow-400">· only the first 20 will run</span>}</span>
              <button onClick={start} disabled={starting || urls.length === 0} className="px-6 py-2.5 rounded-full silver-gradient text-[#050507] font-bold text-sm disabled:opacity-50 inline-flex items-center gap-2" data-testid="bulk-import-start-btn">
                {starting ? "Starting…" : <><Wand2 className="w-4 h-4" /> Import {Math.min(urls.length, 20)} URLs</>}
              </button>
            </div>
          </>
        )}

        {job && (
          <div data-testid="bulk-import-progress">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm">{running ? "Processing…" : "Done"} <span className="text-gray-500">{job.completed}/{job.total}</span></div>
              {!running && Object.keys(job.summary || {}).length > 0 && (
                <div className="flex gap-1.5 flex-wrap">
                  {Object.entries(job.summary).map(([k, n]) => (
                    <span key={k} className={`text-[10px] px-2 py-0.5 rounded ${STATUS_STYLE[k] || "bg-white/5"}`}>{k.replace("_", " ")}: {n}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mb-4">
              <div className="h-full silver-gradient transition-all" style={{ width: `${job.total ? (job.completed / job.total) * 100 : 0}%` }} />
            </div>
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {(job.results || []).map((e, i) => (
                <div key={i} className="flex items-center gap-2 text-xs bg-white/[0.02] rounded-lg px-3 py-2" data-testid={`bulk-result-${i}`}>
                  <span className={`text-[10px] px-2 py-0.5 rounded shrink-0 ${STATUS_STYLE[e.status] || "bg-white/5"}`}>{e.status.replace("_", " ")}</span>
                  <span className="text-gray-500 truncate flex-1">{e.detail || e.url}</span>
                </div>
              ))}
            </div>
            {!running && (
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={reset} className="px-5 py-2.5 rounded-full border border-white/15 text-sm hover:bg-white/5" data-testid="bulk-import-again">Import more</button>
                <button onClick={() => { reset(); onClose(); }} className="px-6 py-2.5 rounded-full silver-gradient text-[#050507] font-bold text-sm" data-testid="bulk-import-done">Go to Review</button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function ReviewEditDialog({ vehicle, onClose, onDone }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (vehicle) {
      setForm({
        ...vehicle,
        image_urls: vehicle.image_urls && vehicle.image_urls.length ? [...vehicle.image_urls] : [""],
      });
    } else setForm(null);
  }, [vehicle]);

  if (!form) return null;
  const isDraft = form.status === "review";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setImg = (i, v) => setForm((f) => { const a = [...(f.image_urls || [])]; a[i] = v; return { ...f, image_urls: a }; });
  const addImg = () => setForm((f) => ({ ...f, image_urls: [...(f.image_urls || []), ""] }));
  const rmImg = (i) => setForm((f) => ({ ...f, image_urls: f.image_urls.filter((_, idx) => idx !== i) }));

  const payload = () => ({
    year: Number(form.year) || 0, make: form.make, model: form.model, trim: form.trim,
    price: Number(form.price) || 0, mileage: Number(form.mileage) || 0, vin: form.vin,
    condition: form.condition, dealer_name: form.dealer_name, location: form.location,
    description: form.description, exterior_color: form.exterior_color, interior_color: form.interior_color,
    zip: form.zip,
    image_urls: (form.image_urls || []).filter((u) => u && u.trim()),
    source_url: form.source_url,
  });

  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/admin/authorized/${form.id}`, payload()); setForm({ ...data, image_urls: data.image_urls?.length ? data.image_urls : [""] }); toast.success("Saved"); onDone && onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const publish = async () => {
    if (!form.make || !form.model || Number(form.price) <= 50) { toast.error("Make, model and a real price are required before publishing"); return; }
    setBusy(true);
    try { await api.put(`/admin/authorized/${form.id}`, payload()); const { data } = await api.post(`/admin/authorized/${form.id}/publish`); toast.success(`Published (${data.badge}) — now live on /inventory`); onDone && onDone(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    finally { setBusy(false); }
  };
  const discard = async () => {
    if (!window.confirm("Discard this vehicle? This cannot be undone.")) return;
    setBusy(true);
    try { await api.delete(`/admin/authorized/${form.id}`); toast.success("Discarded"); onDone && onDone(); onClose(); }
    catch { toast.error("Delete failed"); }
    finally { setBusy(false); }
  };

  const F = (k, label, type = "text") => (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">{label}{form.missing_fields?.includes(k) && <span className="text-yellow-400 ml-1">⚑</span>}</label>
      <input type={type} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="w-full bg-[#050507] border border-[#1A1A24] h-9 rounded-md text-white px-3 text-sm" data-testid={`review-field-${k}`} />
    </div>
  );

  return (
    <Dialog open={!!vehicle} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0A0A0E] border-[#1A1A24] text-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="review-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-light">{isDraft ? "Review & publish" : "Edit vehicle"}</DialogTitle>
          <DialogDescription className="text-gray-400 text-sm">
            Verify every field. Blank flagged (⚑) fields were not found on the source — never auto-filled. Add photos manually if the import missed them. The original listing URL is always kept.
          </DialogDescription>
        </DialogHeader>

        {form.missing_fields?.length > 0 && (
          <div className="mb-3 p-3 rounded-lg bg-yellow-500/5 border border-yellow-500/20 text-xs text-yellow-200">Needs attention — missing: {form.missing_fields.join(", ")}</div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {F("year", "Year", "number")}
          {F("make", "Make")}
          {F("model", "Model")}
          {F("trim", "Trim")}
          {F("price", "Price ($)", "number")}
          {F("mileage", "Mileage", "number")}
          {F("vin", "VIN")}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">Condition</label>
            <select value={form.condition || "used"} onChange={(e) => set("condition", e.target.value)} className="w-full bg-[#050507] border border-[#1A1A24] h-9 rounded-md text-white px-3 text-sm" data-testid="review-field-condition">
              <option value="new">New</option><option value="cpo">CPO</option><option value="used">Used</option>
            </select>
          </div>
          {F("exterior_color", "Exterior Color")}
          {F("interior_color", "Interior Color")}
          {F("dealer_name", "Dealer / Source")}
          {F("location", "Location")}
          {F("zip", "ZIP (for market comps)")}
        </div>

        <div className="mt-3">
          <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">Description</label>
          <textarea value={form.description || ""} onChange={(e) => set("description", e.target.value)} rows={2} className="w-full bg-[#050507] border border-[#1A1A24] rounded-md text-white p-3 text-sm" data-testid="review-field-description" />
        </div>

        <div className="mt-3">
          <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">Photos {form.missing_fields?.includes("image_urls") && <span className="text-yellow-400 ml-1">⚑ none imported — upload manually</span>}</label>
          <div className="space-y-2">
            {(form.image_urls || []).map((u, i) => (
              <div key={i} className="flex gap-2 items-start" data-testid={`review-image-row-${i}`}>
                <div className="flex-1"><ImageInput value={u} onChange={(val) => setImg(i, val)} testId={`review-image-${i}`} compact /></div>
                {u && <img src={u} alt="" className="w-16 h-12 object-cover rounded border border-white/10" />}
                <button onClick={() => rmImg(i)} className="p-2 text-gray-500 hover:text-red-400" data-testid={`review-image-remove-${i}`}><X className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={addImg} className="text-xs px-3 py-1.5 rounded border border-white/10 hover:bg-white/5 inline-flex items-center gap-1.5" data-testid="review-add-image"><Plus className="w-3 h-3" /> Add photo</button>
          </div>
        </div>

        <div className="mt-3">
          <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">Source URL (retained)</label>
          <div className="flex gap-2 items-center">
            <input value={form.source_url || ""} onChange={(e) => set("source_url", e.target.value)} className="flex-1 bg-[#050507] border border-[#1A1A24] h-9 rounded-md text-white px-3 text-xs font-mono" data-testid="review-field-source_url" />
            {form.source_url && <a href={form.source_url} target="_blank" rel="noreferrer" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1">Open <ExternalLink className="w-3 h-3" /></a>}
          </div>
        </div>

        {form.price_history?.length > 0 && (
          <div className="mt-3 text-xs text-gray-400">
            <div className="label-mono mb-1">Price history</div>
            {form.price_history.map((h, i) => <div key={i}>${h.old?.toLocaleString()} → ${h.new?.toLocaleString()} <span className="text-gray-600">· {new Date(h.at).toLocaleDateString()}</span></div>)}
          </div>
        )}

        <div className="flex justify-between gap-2 mt-5">
          <button onClick={discard} disabled={busy} className="px-4 py-2.5 rounded-full border border-red-500/30 text-red-300 text-sm hover:bg-red-500/10 disabled:opacity-50" data-testid="review-discard-btn">Discard</button>
          <div className="flex gap-2">
            <button onClick={save} disabled={busy} className="px-5 py-2.5 rounded-full border border-white/15 text-sm hover:bg-white/5 disabled:opacity-50" data-testid="review-save-btn">Save</button>
            {isDraft && (
              <button onClick={publish} disabled={busy} className="px-6 py-2.5 rounded-full silver-gradient text-[#050507] font-bold text-sm disabled:opacity-50 inline-flex items-center gap-2" data-testid="review-publish-btn">
                <Check className="w-4 h-4" /> Publish
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function AuthorizedFeedTab() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("review");
  const [importOpen, setImportOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);
  const [recheckId, setRecheckId] = useState(null);

  const load = async () => {
    const [r, s] = await Promise.all([
      api.get("/admin/authorized-inventory"),
      api.get("/admin/authorized/stats"),
    ]);
    setRows(r.data); setStats(s.data);
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const ingest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/authorized/ingest-mock");
      toast.success(`Mock feed ingested: ${data.published} published, ${data.exceptions} exceptions`);
      await load();
    } catch { toast.error("Ingest failed"); }
    finally { setBusy(false); }
  };

  const rescore = async (id) => {
    try {
      await api.post(`/admin/authorized/${id}/rescore`);
      toast.success("Rescored");
      await load();
    } catch { toast.error("Rescore failed"); }
  };

  const recheck = async (id) => {
    setRecheckId(id);
    try {
      const { data } = await api.post(`/admin/authorized/${id}/recheck`);
      if (data.monitor_status === "sold" || data.monitor_status === "gone") toast.warning(`Source ${data.monitor_status} — hidden from public & flagged`);
      else if (data.changed) toast.success(`Price updated: $${data.old_price?.toLocaleString()} → $${data.new_price?.toLocaleString()}`);
      else toast.success("Rechecked — no changes");
      await load();
    } catch { toast.error("Recheck failed"); }
    finally { setRecheckId(null); }
  };

  const toggleMonitor = async (id, enabled) => {
    try {
      await api.post(`/admin/authorized/${id}/monitor`, { enabled });
      toast.success(enabled ? "Monitoring on — rechecked every 12h" : "Monitoring off");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update monitoring"); }
  };

  const refreshMarket = async (id) => {
    setRecheckId(id);
    try {
      const { data } = await api.post(`/admin/authorized/${id}/refresh-market`);
      const m = data.market || {};
      if (m.value) toast.success(`555 Market Value $${m.value.toLocaleString()} · ${m.rating} (${m.source})`);
      else toast.warning("No reliable market data found");
      await load();
    } catch { toast.error("Market refresh failed"); }
    finally { setRecheckId(null); }
  };

  const reviewCount = rows.filter((r) => r.status === "review").length;
  const filtered = rows.filter((r) => filter === "all" || r.status === filter);

  return (
    <div data-testid="authorized-feed-tab">
      <div className="mb-6 p-4 rounded-2xl bg-yellow-500/5 border border-yellow-500/20 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
        <div className="text-sm text-yellow-200/90">
          <div className="font-medium mb-1">MOCKED FEED — No live data providers connected</div>
          <div className="text-xs text-yellow-200/70">All rows below are simulated. Replace <code className="text-yellow-100">MOCK_FEED_v1</code> with Marketcheck / vAuto / DataOne once contracts are signed. The pipeline (VIN decode, anomaly detection, exception routing) is production-ready.</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        {stats && (
          <>
            <div className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10">Total <span className="text-white font-bold ml-1">{stats.total}</span></div>
            <div className="text-xs px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">Published <span className="font-bold ml-1">{stats.published}</span></div>
            <div className="text-xs px-3 py-1.5 rounded-full bg-yellow-500/10 text-yellow-300 border border-yellow-500/20">Exceptions <span className="font-bold ml-1">{stats.exceptions}</span></div>
            <div className="text-xs px-3 py-1.5 rounded-full bg-red-500/10 text-red-300 border border-red-500/20">Rejected <span className="font-bold ml-1">{stats.rejected}</span></div>
          </>
        )}
        <div className="ml-auto flex gap-2">
          <button
            onClick={() => setBulkOpen(true)}
            className="px-5 py-2 rounded-full border border-emerald-500/40 text-emerald-300 font-medium text-sm hover:bg-emerald-500/10 inline-flex items-center gap-2"
            data-testid="bulk-import-btn"
          >
            <LinkIcon className="w-3.5 h-3.5" /> Bulk Import URLs
          </button>
          <button
            onClick={() => setImportOpen(true)}
            className="px-5 py-2 rounded-full border border-white/15 text-white font-medium text-sm hover:bg-white/5 inline-flex items-center gap-2"
            data-testid="import-url-btn"
          >
            <LinkIcon className="w-3.5 h-3.5" /> Import Single URL
          </button>
          <button
            onClick={ingest}
            disabled={busy}
            className="px-5 py-2 rounded-full silver-gradient text-[#050507] font-bold text-sm disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="ingest-mock-feed-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
            {busy ? "Ingesting…" : "Ingest Mock Feed"}
          </button>
        </div>
      </div>

      <ImportUrlDialog open={importOpen} onClose={() => setImportOpen(false)} onDone={() => { setFilter("review"); load(); }} />
      <BulkImportDialog open={bulkOpen} onClose={() => setBulkOpen(false)} onDone={() => { setFilter("review"); load(); }} />
      <ReviewEditDialog vehicle={editVehicle} onClose={() => setEditVehicle(null)} onDone={load} />

      <div className="flex gap-2 mb-4">
        {["review", "all", "published", "exception", "rejected"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`text-xs px-3 py-1.5 rounded-full border ${filter === f ? "border-white bg-white/10" : "border-white/10 text-gray-400 hover:bg-white/5"}`}
            data-testid={`authorized-filter-${f}`}
          >
            {f === "review" ? `import review${reviewCount ? ` (${reviewCount})` : ""}` : f}
          </button>
        ))}
      </div>

      <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-gray-400 text-xs">
            <tr>
              <th className="text-left p-3">VIN</th>
              <th className="text-left p-3">Vehicle</th>
              <th className="text-left p-3">Price</th>
              <th className="text-left p-3">Median Δ</th>
              <th className="text-left p-3">Badge</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Dealer</th>
              <th className="text-left p-3">Source</th>
              <th className="text-left p-3">Watched</th>
              <th className="text-left p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-gray-500">{filter === "review" ? "No vehicles awaiting review. Use “Bulk Import URLs” to add some." : "No rows. Click “Ingest Mock Feed” to seed 19 sample vehicles."}</td></tr>}
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-white/5" data-testid={`authorized-row-${r.id}`}>
                <td className="p-3 font-mono text-xs text-gray-400">{r.vin ? r.vin.slice(-8) : <span className="text-gray-600">—</span>}</td>
                <td className="p-3">
                  <div className="font-medium">{r.year || "—"} {r.make} {r.model}</div>
                  <div className="text-xs text-gray-500">{r.trim} · {r.condition?.toUpperCase()} · {r.mileage?.toLocaleString()} mi</div>
                  {r.missing_fields?.length > 0 && (
                    <div className="text-[10px] text-yellow-400 mt-0.5" data-testid={`missing-flags-${r.id}`}>⚑ missing: {r.missing_fields.join(", ")}</div>
                  )}
                  {r.monitor_status && r.monitor_status !== "active" && (
                    <div className="text-[10px] text-orange-400 mt-0.5">monitor: {r.monitor_status}</div>
                  )}
                  {r.price_history?.length > 0 && (
                    <div className="text-[10px] text-gray-500 mt-0.5">price history: {r.price_history.length} change{r.price_history.length > 1 ? "s" : ""}</div>
                  )}
                </td>
                <td className="p-3 font-mono">{r.price ? `$${r.price.toLocaleString()}` : <span className="text-gray-600">—</span>}</td>
                <td className="p-3 text-xs">
                  {r.price_delta_pct != null ? (
                    <span className={r.price_delta_pct < 0 ? "text-emerald-400" : "text-red-400"}>
                      {(r.price_delta_pct * 100).toFixed(1)}%
                    </span>
                  ) : <span className="text-gray-600">—</span>}
                </td>
                <td className="p-3">
                  <BadgePill value={r.badge} />
                </td>
                <td className="p-3">
                  <StatusPill value={r.status} />
                </td>
                <td className="p-3 text-xs text-gray-400">{r.dealer_name}<div className="text-[10px] text-gray-600">{r.location}</div></td>
                <td className="p-3 text-xs">
                  {r.source_url ? (
                    <a href={r.source_url} target="_blank" rel="noreferrer" className="px-2 py-0.5 rounded bg-white/5 text-gray-300 text-[10px] hover:bg-white/10 inline-block max-w-[120px] truncate align-middle" data-testid={`source-link-${r.id}`}>{r.is_mocked ? "MOCKED" : r.source_feed}</a>
                  ) : (
                    <span className="px-2 py-0.5 rounded bg-yellow-500/10 text-yellow-300 font-mono text-[10px]">{r.is_mocked ? "MOCKED" : r.source_feed}</span>
                  )}
                </td>
                <td className="p-3">
                  {r.status === "published" && r.source_feed === "URL_IMPORT" && r.source_url ? (
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => toggleMonitor(r.id, !r.monitor_enabled)}
                        role="switch"
                        aria-checked={!!r.monitor_enabled}
                        className={`relative w-9 h-5 rounded-full transition-colors ${r.monitor_enabled ? "bg-emerald-500" : "bg-white/15"}`}
                        data-testid={`monitor-toggle-${r.id}`}
                        title={r.monitor_enabled ? "Monitoring on" : "Monitoring off"}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${r.monitor_enabled ? "left-4" : "left-0.5"}`} />
                      </button>
                      <span className="text-[9px] text-gray-500">{r.last_monitored_at ? `checked ${new Date(r.last_monitored_at).toLocaleDateString()}` : "not yet"}</span>
                    </div>
                  ) : (
                    <span className="text-gray-600 text-xs">—</span>
                  )}
                </td>
                <td className="p-3">
                  <div className="flex gap-1.5 justify-end">
                    {r.status === "review" ? (
                      <button onClick={() => setEditVehicle(r)} className="text-xs px-2.5 py-1 rounded silver-gradient text-[#050507] font-bold" data-testid={`review-publish-${r.id}`}>Review &amp; Publish</button>
                    ) : (
                      <>
                        {r.source_feed === "URL_IMPORT" && r.source_url && (
                          <button onClick={() => recheck(r.id)} disabled={recheckId === r.id} className="text-xs px-2 py-1 rounded border border-white/10 hover:bg-white/5 disabled:opacity-50" data-testid={`recheck-${r.id}`}>{recheckId === r.id ? "…" : "Re-check"}</button>
                        )}
                        <button onClick={() => refreshMarket(r.id)} disabled={recheckId === r.id} className="text-xs px-2 py-1 rounded border border-white/10 hover:bg-white/5 disabled:opacity-50" data-testid={`refresh-market-${r.id}`} title="Refresh 555 Market Value from MarketCheck">Market $</button>
                        <button onClick={() => setEditVehicle(r)} className="text-xs px-2 py-1 rounded border border-white/10 hover:bg-white/5" data-testid={`edit-${r.id}`}>Edit</button>
                        <button onClick={() => rescore(r.id)} className="text-xs px-2 py-1 rounded border border-white/10 hover:bg-white/5" data-testid={`rescore-${r.id}`}>Rescore</button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ExceptionQueueTab() {
  const [rows, setRows] = useState([]);
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/authorized/exceptions");
    setRows(data);
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const approve = async (id) => {
    try {
      await api.post(`/admin/authorized/exceptions/${id}/approve`);
      toast.success("Approved & published");
      await load();
    } catch { toast.error("Approve failed"); }
  };
  const reject = async () => {
    if (!rejectFor) return;
    try {
      await api.post(`/admin/authorized/exceptions/${rejectFor}/reject`, { reason });
      toast.success("Rejected");
      setRejectFor(null); setReason("");
      await load();
    } catch { toast.error("Reject failed"); }
  };

  return (
    <div data-testid="exception-queue-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-display text-xl">Exception Queue</div>
          <div className="text-xs text-gray-500 mt-1">Rows flagged by the anomaly detector (price outside ±median, missing photos, etc.). Approve to publish or reject with a reason.</div>
        </div>
        <div className="text-xs px-3 py-1.5 rounded-full bg-yellow-500/10 text-yellow-300">{rows.length} pending</div>
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-white/10 rounded-2xl text-gray-500" data-testid="exceptions-empty">
          <Check className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
          Queue clear. Everything looks good.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {rows.map((r) => (
            <div key={r.id} className="bg-[#0A0A0E] border border-yellow-500/30 rounded-2xl p-5" data-testid={`exception-card-${r.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="font-display text-lg">{r.year} {r.make} {r.model}</div>
                  <div className="text-xs text-gray-500">{r.trim} · VIN {r.vin?.slice(-8)}</div>
                </div>
                <span className="text-xs px-2 py-0.5 rounded bg-yellow-500/10 text-yellow-300 font-mono">{r.is_mocked ? "MOCKED" : r.source_feed}</span>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
                <div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider">Asking</div>
                  <div className="font-mono">${r.price?.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider">Median</div>
                  <div className="font-mono text-gray-400">{r.price_median ? `$${r.price_median.toLocaleString()}` : "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider">Delta</div>
                  <div className={`font-mono ${r.price_delta_pct < 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {r.price_delta_pct != null ? `${(r.price_delta_pct * 100).toFixed(1)}%` : "—"}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-4">
                {r.anomaly_flags?.map((f) => (
                  <span key={f} className="text-[10px] px-2 py-1 rounded bg-red-500/10 text-red-300 border border-red-500/20">
                    {f.replace(/_/g, " ")}
                  </span>
                ))}
              </div>

              <div className="text-xs text-gray-500 mb-4">
                {r.dealer_name} · {r.location} · {r.mileage?.toLocaleString()} mi · {r.condition?.toUpperCase()}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => approve(r.id)}
                  className="flex-1 py-2 rounded-full silver-gradient text-[#050507] font-bold text-xs inline-flex items-center justify-center gap-1"
                  data-testid={`approve-exception-${r.id}`}
                >
                  <Check className="w-3.5 h-3.5" /> Approve & Publish
                </button>
                <button
                  onClick={() => { setRejectFor(r.id); setReason(""); }}
                  className="flex-1 py-2 rounded-full border border-white/15 text-xs hover:bg-white/5 inline-flex items-center justify-center gap-1"
                  data-testid={`reject-exception-${r.id}`}
                >
                  <X className="w-3.5 h-3.5" /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!rejectFor} onOpenChange={(v) => !v && setRejectFor(null)}>
        <DialogContent className="bg-[#0A0A0E] border-[#1A1A24] text-white">
          <DialogHeader>
            <DialogTitle>Reject listing</DialogTitle>
            <DialogDescription className="text-gray-500">Provide a reason (visible to internal ops).</DialogDescription>
          </DialogHeader>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. price too low, likely typo" className="bg-[#050507] border-[#1A1A24] text-white" data-testid="reject-reason-input" />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setRejectFor(null)} className="px-4 py-2 rounded-full border border-white/15 text-sm">Cancel</button>
            <button onClick={reject} className="px-5 py-2 rounded-full silver-gradient text-[#050507] font-bold text-sm" data-testid="confirm-reject-btn">Reject</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BadgePill({ value }) {
  const map = {
    "Great Deal": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    "Fair Price": "bg-blue-500/10 text-blue-300 border-blue-500/20",
    "Authorized": "bg-white/5 text-gray-300 border-white/10",
    "Anomaly": "bg-yellow-500/10 text-yellow-300 border-yellow-500/20",
  };
  return <span className={`text-[10px] px-2 py-0.5 rounded border ${map[value] || map.Authorized}`}>{value}</span>;
}

function StatusPill({ value }) {
  const map = {
    published: "bg-emerald-500/10 text-emerald-300",
    review: "bg-blue-500/10 text-blue-300",
    exception: "bg-yellow-500/10 text-yellow-300",
    rejected: "bg-red-500/10 text-red-300",
  };
  return <span className={`text-[10px] px-2 py-0.5 rounded ${map[value] || "bg-white/5"}`}>{value}</span>;
}


// =============================================================================
// Leads Tab (with conversation chronology + admin note composer)
// =============================================================================
function LeadsTab({ leads, onReload }) {
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [noteBusy, setNoteBusy] = useState(false);
  const [note, setNote] = useState("");

  const toggle = async (leadId) => {
    if (expanded === leadId) { setExpanded(null); setDetail(null); return; }
    setExpanded(leadId);
    setDetail(null);
    try {
      const { data } = await api.get(`/admin/leads/${leadId}`);
      setDetail(data);
    } catch { toast.error("Couldn't load thread"); }
  };

  const addNote = async () => {
    if (!note.trim() || !detail) return;
    setNoteBusy(true);
    try {
      await api.post(`/admin/leads/${detail.id}/note`, { body: note });
      setNote("");
      const { data } = await api.get(`/admin/leads/${detail.id}`);
      setDetail(data);
      toast.success("Note added");
    } catch { toast.error("Failed"); }
    finally { setNoteBusy(false); }
  };

  if (leads.length === 0) {
    return <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-10 text-center text-gray-500">No leads yet</div>;
  }

  return (
    <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-white/5 text-gray-400">
          <tr>
            <th className="text-left p-4">Date</th>
            <th className="text-left p-4">Type</th>
            <th className="text-left p-4">Contact</th>
            <th className="text-left p-4">Summary</th>
            <th className="text-left p-4">Status</th>
            <th className="text-left p-4">Thread</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((l) => {
            const convo = l.conversation || [];
            return (
              <LeadRow
                key={l.id}
                lead={l}
                convo={convo}
                expanded={expanded === l.id}
                detail={expanded === l.id ? detail : null}
                onToggle={() => toggle(l.id)}
                note={note}
                setNote={setNote}
                onAddNote={addNote}
                noteBusy={noteBusy}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function LeadRow({ lead: l, convo, expanded, detail, onToggle, note, setNote, onAddNote, noteBusy }) {
  return (
    <>
      <tr className="border-t border-white/5" data-testid={`lead-row-${l.id}`}>
        <td className="p-4 text-gray-400 text-xs">{new Date(l.created_at).toLocaleString()}</td>
        <td className="p-4"><span className="label-mono">{l.type.replace(/_/g, " ")}</span></td>
        <td className="p-4">
          <div>{l.payload?.name || l.payload?.email || "-"}</div>
          <div className="text-xs text-gray-500">{l.payload?.email}</div>
        </td>
        <td className="p-4 text-xs text-gray-400 max-w-xs truncate">
          {l.payload?.title || `${l.payload?.year || ""} ${l.payload?.make || ""} ${l.payload?.model || ""}`.trim() || l.payload?.message || "—"}
        </td>
        <td className="p-4">
          <span className={`text-xs px-2 py-1 rounded-full ${l.status === "responded" ? "bg-blue-500/20 text-blue-300" : "bg-emerald-500/20 text-emerald-400"}`}>{l.status || "new"}</span>
        </td>
        <td className="p-4">
          <button
            onClick={onToggle}
            className="text-xs px-3 py-1 rounded-full border border-white/10 hover:bg-white/5 inline-flex items-center gap-1"
            data-testid={`lead-thread-toggle-${l.id}`}
          >
            <MessageSquare className="w-3 h-3" /> {convo.length || 0}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-[#050507]" data-testid={`lead-thread-${l.id}`}>
          <td colSpan={6} className="p-6">
            {!detail ? (
              <div className="text-gray-500 text-sm text-center py-6">Loading thread…</div>
            ) : (
              <div className="max-w-3xl">
                <div className="space-y-2 mb-5">
                  {(detail.conversation || []).length === 0 ? (
                    <div className="text-gray-500 text-sm py-4 border border-dashed border-white/10 rounded-lg text-center">
                      No messages yet. When the broker clicks the reply link in their notification email, their response lands here.
                    </div>
                  ) : (
                    detail.conversation.map((m) => {
                      const isBroker = m.direction === "broker_out";
                      return (
                        <div key={m.id} className={`p-3 rounded-lg border ${isBroker ? "bg-emerald-500/5 border-emerald-500/20" : "bg-white/5 border-white/10"}`}>
                          <div className="flex items-baseline justify-between mb-1">
                            <span className={`text-[10px] uppercase tracking-wider ${isBroker ? "text-emerald-300" : "text-gray-400"}`}>{m.from}</span>
                            <span className="text-[10px] text-gray-600">{new Date(m.sent_at).toLocaleString()}</span>
                          </div>
                          <div className="text-xs text-gray-200 whitespace-pre-wrap leading-relaxed">{m.body}</div>
                        </div>
                      );
                    })
                  )}
                </div>
                <div className="border-t border-white/5 pt-4">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Add note or paste external reply</div>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={3}
                    placeholder="Internal note, or paste a broker's Gmail reply here to record it in the thread."
                    className="w-full bg-[#0A0A0E] border border-[#1A1A24] rounded-lg p-3 text-xs text-white"
                    data-testid="lead-note-input"
                  />
                  <button
                    onClick={onAddNote}
                    disabled={noteBusy || !note.trim()}
                    className="mt-2 text-xs px-4 py-1.5 rounded-full silver-gradient text-[#050507] font-bold disabled:opacity-40 inline-flex items-center gap-1"
                    data-testid="lead-note-submit"
                  >
                    <Plus className="w-3 h-3" /> {noteBusy ? "Adding…" : "Append to thread"}
                  </button>
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

